/**
 * ScenarioCompareModalV2 - Mission Control Dashboard
 *
 * A redesigned comparison interface that prioritizes visual comprehension
 * over tabular data. Uses charts, progress bars, and visual scoring to
 * help users quickly identify the best scenario and understand trade-offs.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Button,
    Typography,
    IconButton,
    Chip,
    Tooltip,
    alpha,
    useTheme,
    CircularProgress,
    TextField,
    Collapse,
} from '@mui/material';
import {
    Close as CloseIcon,
    EmojiEvents as TrophyIcon,
    TrendingUp as TrendingUpIcon,
    Battery80 as BatteryIcon,
    WbSunny as SolarIcon,
    LocalGasStation as GeneratorIcon,
    AccountBalance as CapexIcon,
    Timeline as TimelineIcon,
    Speed as SpeedIcon,
    TableChart as TableIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    GridOn as GridOnIcon,
    PictureAsPdf as PdfIcon,
    ViewModule as BasicIcon,
    Dashboard as AdvancedIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Share as ShareIcon,
    AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import { ShareConfigDialog, exportInvestmentModel, exportFullData, exportPresentation, vortxColors, useThemeMode, useResponsive, monthlyPerformanceService, PowerGraph, debug, vortxTypography, calculateCapex, calculateItc, calculateOpex } from '@vortx/shared-ui';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    Legend,
    BarChart,
    Bar,
    Cell,
} from 'recharts';
import { reoptToDailySeries } from '../reoptTransform';
import InteractiveEconomicsPanel from './InteractiveEconomicsPanel';

// Extended color palette for scenarios
const SCENARIO_COLORS = [
    '#b28c50', // Vortx Gold
    '#5fd4c4', // Teal
    '#8b9dc3', // Muted Blue
    '#c4a35a', // Warm Gold
    '#6b9080', // Sage
];

// Score weighting for overall ranking (can be customized)
const SCORE_WEIGHTS = {
    npv: 0.30,
    payback: 0.25,
    savings: 0.20,
    capex: 0.15,
    gridReduction: 0.10,
};

/**
 * Calculate a normalized score (0-100) for each scenario
 */
function calculateScenarioScores(scenarios, getFinancialSummary, getSystemConfig, getPerformanceData) {
    const metrics = scenarios.map(s => {
        const financial = getFinancialSummary(s);
        const config = getSystemConfig(s);
        const performance = getPerformanceData(s);
        return {
            id: s.id,
            name: s.name,
            npv: financial.npv || 0,
            payback: financial.paybackPeriod || 999,
            savings: financial.annualSavings || 0,
            capex: financial.totalCapex || 0,
            gridReduction: performance.gridReduction || 0,
            pvKw: config.pvKw || 0,
            storageKw: config.storageKw || 0,
            storageKwh: config.storageKwh || 0,
        };
    });

    // Find min/max for normalization
    const ranges = {
        npv: { min: Math.min(...metrics.map(m => m.npv)), max: Math.max(...metrics.map(m => m.npv)) },
        payback: { min: Math.min(...metrics.map(m => m.payback)), max: Math.max(...metrics.map(m => m.payback)) },
        savings: { min: Math.min(...metrics.map(m => m.savings)), max: Math.max(...metrics.map(m => m.savings)) },
        capex: { min: Math.min(...metrics.map(m => m.capex)), max: Math.max(...metrics.map(m => m.capex)) },
        gridReduction: { min: Math.min(...metrics.map(m => m.gridReduction)), max: Math.max(...metrics.map(m => m.gridReduction)) },
    };

    // Normalize scores using min-max scaling but with a floor (30-100 range)
    // This shows meaningful differences between scenarios while never showing 0%
    const SCORE_FLOOR = 30; // Worst case shows as 30%, not 0%
    const SCORE_RANGE = 100 - SCORE_FLOOR; // 70 points of range

    return metrics.map(m => {
        const normalize = (val, min, max, inverse = false) => {
            if (max === min) return 65; // Midpoint if all values are equal
            // Map to 0-1, then scale to SCORE_FLOOR-100
            let norm = (val - min) / (max - min);
            if (inverse) norm = 1 - norm;
            return SCORE_FLOOR + (norm * SCORE_RANGE);
        };

        // Clamp scores to valid range
        const clamp = (val) => Math.max(SCORE_FLOOR, Math.min(100, val));

        const npvScore = clamp(normalize(m.npv, ranges.npv.min, ranges.npv.max));
        const paybackScore = clamp(normalize(m.payback, ranges.payback.min, ranges.payback.max, true)); // Lower is better
        const savingsScore = clamp(normalize(m.savings, ranges.savings.min, ranges.savings.max));
        const capexScore = clamp(normalize(m.capex, ranges.capex.min, ranges.capex.max, true)); // Lower is better
        const gridScore = clamp(normalize(m.gridReduction, ranges.gridReduction.min, ranges.gridReduction.max));

        const totalScore =
            npvScore * SCORE_WEIGHTS.npv +
            paybackScore * SCORE_WEIGHTS.payback +
            savingsScore * SCORE_WEIGHTS.savings +
            capexScore * SCORE_WEIGHTS.capex +
            gridScore * SCORE_WEIGHTS.gridReduction;

        return {
            ...m,
            scores: { npv: npvScore, payback: paybackScore, savings: savingsScore, capex: capexScore, gridReduction: gridScore },
            totalScore: Math.round(totalScore),
        };
    });
}

/**
 * Circular Score Ring Component
 */
function ScoreRing({ score, size = 120, strokeWidth = 8, color, isWinner = false }) {
    const { isRetro } = useThemeMode();
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (score / 100) * circumference;

    return (
        <Box sx={{ position: 'relative', width: size, height: size }}>
            <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                {/* Background ring */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={isRetro ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}
                    strokeWidth={strokeWidth}
                />
                {/* Score ring */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    style={{
                        transition: 'stroke-dashoffset 1s ease-out',
                        filter: isWinner ? `drop-shadow(0 0 12px ${color})` : 'none',
                    }}
                />
            </svg>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                }}
            >
                <Typography
                    sx={{
                        fontSize: size * 0.28,
                        fontWeight: 700,
                        fontFamily: vortxTypography.mono,
                        color: color,
                        lineHeight: 1,
                        textShadow: isWinner ? `0 0 20px ${alpha(color, 0.5)}` : 'none',
                    }}
                >
                    {score}
                </Typography>
                <Typography
                    sx={{
                        fontSize: size * 0.1,
                        fontWeight: 500,
                        color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                    }}
                >
                    Score
                </Typography>
            </Box>
            {isWinner && (
                <TrophyIcon
                    sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        color: '#ffd700',
                        fontSize: 28,
                        filter: 'drop-shadow(0 0 8px rgba(255,215,0,0.6))',
                    }}
                />
            )}
        </Box>
    );
}

/**
 * Metric Comparison Bar
 */
function MetricBar({ label, scenarios, getValue, format, higherIsBetter = true, icon: Icon }) {
    const { isRetro } = useThemeMode();
    const values = scenarios.map(s => getValue(s));
    const maxVal = Math.max(...values.filter(v => v != null && !isNaN(v)));
    const minVal = Math.min(...values.filter(v => v != null && !isNaN(v)));

    // Find best scenario index
    const bestIdx = higherIsBetter
        ? values.indexOf(Math.max(...values.filter(v => v != null)))
        : values.indexOf(Math.min(...values.filter(v => v != null && v > 0)));

    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                {Icon && <Icon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }} />}
                <Typography
                    sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                    }}
                >
                    {label}
                </Typography>
            </Box>
            {scenarios.map((s, idx) => {
                const value = values[idx];
                const percentage = maxVal > 0 ? ((value || 0) / maxVal) * 100 : 0;
                const isBest = idx === bestIdx && value != null && value > 0;

                return (
                    <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Typography
                            sx={{
                                width: 100,
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                color: SCENARIO_COLORS[idx],
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {s.name}
                        </Typography>
                        <Box sx={{ flex: 1, position: 'relative', height: 24 }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    bgcolor: isRetro ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                    borderRadius: 1,
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    width: `${Math.min(percentage, 100)}%`,
                                    bgcolor: alpha(SCENARIO_COLORS[idx], 0.3),
                                    borderRadius: 1,
                                    transition: 'width 0.8s ease-out',
                                    borderRight: isBest ? `3px solid ${SCENARIO_COLORS[idx]}` : 'none',
                                    boxShadow: isBest ? `0 0 12px ${alpha(SCENARIO_COLORS[idx], 0.4)}` : 'none',
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 1.5,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        fontFamily: vortxTypography.mono,
                                        color: isBest ? SCENARIO_COLORS[idx] : isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                    }}
                                >
                                    {format ? format(value) : value}
                                </Typography>
                                {isBest && (
                                    <Chip
                                        label="BEST"
                                        size="small"
                                        sx={{
                                            ml: 'auto',
                                            height: 18,
                                            fontSize: '0.6rem',
                                            fontWeight: 700,
                                            bgcolor: alpha(SCENARIO_COLORS[idx], 0.2),
                                            color: SCENARIO_COLORS[idx],
                                            border: `1px solid ${alpha(SCENARIO_COLORS[idx], 0.4)}`,
                                            '& .MuiChip-label': { px: 1 },
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

/**
 * Cash Flow Chart Component
 */
function CashFlowChart({ scenarios, getCashFlows, getFinancialSummary }) {
    const { isRetro } = useThemeMode();
    const chartData = useMemo(() => {
        // Pre-compute cumulative cash flows for each scenario
        // This handles cases where saved data doesn't have cumulative values
        const scenarioCumulatives = scenarios.map(s => {
            const flows = getCashFlows(s);
            const financial = getFinancialSummary(s);
            const initialInvestment = financial.totalCapex || 0;

            // Check if cumulative values are missing or all zeros
            const hasCumulative = flows.some(f => f.cumulative && f.cumulative !== 0);

            if (hasCumulative) {
                // Use saved cumulative values
                return { flows, initialInvestment, computed: false };
            } else {
                // Compute cumulative from net_cashflow
                let cumulative = -initialInvestment;
                const computedFlows = flows.map(f => {
                    cumulative += (f.net_cashflow ?? f.netCashflow ?? 0);
                    return { ...f, cumulative };
                });
                return { flows: computedFlows, initialInvestment, computed: true };
            }
        });

        // Determine max years from actual cash flow data
        let maxYear = 0;
        scenarioCumulatives.forEach(({ flows }) => {
            flows.forEach(f => {
                if (f.year > maxYear) maxYear = f.year;
            });
        });
        // Default to 20 years if no cash flow data found
        if (maxYear === 0) maxYear = 20;

        // Build year-by-year data combining all scenarios
        // Start from year 0 (initial investment) through maxYear
        const years = Array.from({ length: maxYear + 1 }, (_, i) => i);
        return years.map(year => {
            const point = { year };
            scenarioCumulatives.forEach(({ flows, initialInvestment }, idx) => {
                if (year === 0) {
                    // Year 0: Initial investment (negative of total capex)
                    point[`scenario${idx}`] = -initialInvestment;
                } else {
                    const yearFlow = flows.find(f => f.year === year);
                    point[`scenario${idx}`] = yearFlow?.cumulative ?? 0;
                }
            });
            return point;
        });
    }, [scenarios, getCashFlows, getFinancialSummary]);

    return (
        <Box sx={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
                <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <defs>
                        {scenarios.map((_, idx) => (
                            <linearGradient key={idx} id={`gradient${idx}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={SCENARIO_COLORS[idx]} stopOpacity={0.4} />
                                <stop offset="100%" stopColor={SCENARIO_COLORS[idx]} stopOpacity={0.05} />
                            </linearGradient>
                        ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={isRetro ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"} />
                    <XAxis
                        dataKey="year"
                        stroke={isRetro ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"}
                        tick={{ fill: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: 11 }}
                        tickLine={{ stroke: isRetro ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)' }}
                        label={{ value: 'Year', position: 'bottom', fill: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: 11 }}
                    />
                    <YAxis
                        stroke={isRetro ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"}
                        tick={{ fill: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: 11 }}
                        tickLine={{ stroke: isRetro ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)' }}
                        tickFormatter={(val) => `$${(val / 1000).toFixed(0)}k`}
                    />
                    <RechartsTooltip
                        contentStyle={{
                            backgroundColor: isRetro ? 'rgba(20,20,20,0.95)' : isRetro ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.95)',
                            border: '1px solid rgba(178,140,80,0.3)',
                            borderRadius: 4,
                            fontSize: 12,
                        }}
                        labelStyle={{ color: '#b28c50', fontWeight: 600 }}
                        formatter={(value, name) => [
                            `$${(value || 0).toLocaleString()}`,
                            scenarios[parseInt(name.replace('scenario', ''))]?.name || name
                        ]}
                        labelFormatter={(year) => year === 0 ? 'Initial Investment' : `Year ${year}`}
                    />
                    {scenarios.map((s, idx) => (
                        <Area
                            key={s.id}
                            type="monotone"
                            dataKey={`scenario${idx}`}
                            name={`scenario${idx}`}
                            stroke={SCENARIO_COLORS[idx]}
                            strokeWidth={2}
                            fill={`url(#gradient${idx})`}
                        />
                    ))}
                </AreaChart>
            </ResponsiveContainer>
        </Box>
    );
}

/**
 * Radar Chart for Multi-Dimensional Comparison
 */
function ComparisonRadar({ scoredScenarios }) {
    const { isRetro } = useThemeMode();
    // Define metrics with full names for clarity
    const metrics = [
        { key: 'npv', label: 'Net Present Value' },
        { key: 'payback', label: 'Payback Period' },
        { key: 'savings', label: 'Annual Savings' },
        { key: 'capex', label: 'Cost Efficiency' },
        { key: 'gridReduction', label: 'Grid Independence' },
    ];

    const radarData = metrics.map(({ key, label }) => {
        const point = { metric: label, fullMark: 100 };
        scoredScenarios.forEach((s, idx) => {
            point[s.name] = s.scores[key] || 0;
        });
        return point;
    });

    return (
        <Box sx={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
                <RadarChart data={radarData} margin={{ top: 40, right: 80, bottom: 40, left: 80 }} cx="50%" cy="45%">
                    <PolarGrid
                        stroke={isRetro ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"}
                        gridType="polygon"
                    />
                    <PolarAngleAxis
                        dataKey="metric"
                        tick={({ x, y, payload, index }) => {
                            // Position labels directly aligned with their points
                            // 5 points: 0=top, 1=upper-right, 2=lower-right, 3=lower-left, 4=upper-left
                            const labelConfig = [
                                { x: 0, y: -16, anchor: 'middle' },    // 0: Net Present Value - centered above
                                { x: 12, y: 0, anchor: 'start' },      // 1: Payback Period - to the right
                                { x: 12, y: 4, anchor: 'start' },      // 2: Annual Savings - to the right
                                { x: -12, y: 4, anchor: 'end' },       // 3: Cost Efficiency - to the left
                                { x: -12, y: 0, anchor: 'end' },       // 4: Grid Independence - to the left
                            ];
                            const config = labelConfig[index] || { x: 0, y: 0, anchor: 'middle' };
                            return (
                                <text
                                    x={x + config.x}
                                    y={y + config.y}
                                    fill={isRetro ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.85)"}
                                    fontSize={11}
                                    fontWeight={500}
                                    textAnchor={config.anchor}
                                    dominantBaseline="middle"
                                >
                                    {payload.value}
                                </text>
                            );
                        }}
                        tickLine={false}
                    />
                    <PolarRadiusAxis
                        angle={90}
                        domain={[0, 100]}
                        tick={{ fill: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: 9 }}
                        tickCount={4}
                        axisLine={false}
                    />
                    {scoredScenarios.map((s, idx) => (
                        <Radar
                            key={s.id}
                            name={s.name}
                            dataKey={s.name}
                            stroke={SCENARIO_COLORS[idx]}
                            fill={SCENARIO_COLORS[idx]}
                            fillOpacity={0.25}
                            strokeWidth={2.5}
                            dot={{
                                r: 4,
                                fill: SCENARIO_COLORS[idx],
                                strokeWidth: 0,
                            }}
                            activeDot={{
                                r: 6,
                                fill: SCENARIO_COLORS[idx],
                                stroke: isRetro ? '#fff' : '#2c2c2c',
                                strokeWidth: 2,
                            }}
                        />
                    ))}
                    <Legend
                        wrapperStyle={{
                            paddingTop: 15,
                        }}
                        iconType="circle"
                        iconSize={10}
                        formatter={(value) => (
                            <span style={{
                                color: isRetro ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                                fontSize: 12,
                                fontWeight: 500,
                                marginLeft: 4,
                            }}>
                                {value}
                            </span>
                        )}
                    />
                    <RechartsTooltip
                        contentStyle={{
                            backgroundColor: isRetro ? 'rgba(30, 30, 30, 0.95)' : isRetro ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.95)',
                            border: isRetro ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)',
                            borderRadius: 8,
                            fontSize: 12,
                            padding: '8px 12px',
                        }}
                        labelStyle={{
                            color: isRetro ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                            fontWeight: 600,
                            marginBottom: 4,
                        }}
                        itemStyle={{
                            color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                            padding: '2px 0',
                        }}
                        formatter={(value, name) => [`${Math.round(value)}%`, name]}
                    />
                </RadarChart>
            </ResponsiveContainer>
        </Box>
    );
}

/**
 * System Configuration Visual
 */
function SystemConfigVisual({ scenarios, getSystemConfig }) {
    const { isRetro } = useThemeMode();
    return (
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
            {scenarios.map((s, idx) => {
                const config = getSystemConfig(s);
                return (
                    <Box
                        key={s.id}
                        sx={{
                            flex: '1 1 200px',
                            maxWidth: 280,
                            p: 2.5,
                            borderRadius: 2,
                            bgcolor: isRetro ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                            border: `1px solid ${alpha(SCENARIO_COLORS[idx], 0.3)}`,
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Glow effect */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 3,
                                bgcolor: SCENARIO_COLORS[idx],
                                boxShadow: `0 0 20px ${alpha(SCENARIO_COLORS[idx], 0.4)}`,
                            }}
                        />

                        <Typography
                            sx={{
                                fontSize: '0.9rem',
                                fontWeight: 700,
                                color: SCENARIO_COLORS[idx],
                                mb: 2,
                            }}
                        >
                            {s.name}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {/* Existing Solar - show if present */}
                            {config.existingSolarKw > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <SolarIcon sx={{ fontSize: 20, color: '#a0a0a0' }} />
                                    <Box>
                                        <Typography sx={{ fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                            Existing Solar
                                        </Typography>
                                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
                                            {config.existingSolarKw.toLocaleString()} kW
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* New Solar - only show if this scenario has PV */}
                            {config.pvKw > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <SolarIcon sx={{ fontSize: 20, color: '#ffd700' }} />
                                    <Box>
                                        <Typography sx={{ fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                            {config.existingSolarKw > 0 ? 'New Solar' : 'Solar'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)' }}>
                                            {config.pvKw.toLocaleString()} kW
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* Battery - only show if this scenario has storage */}
                            {config.storageKw > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <BatteryIcon sx={{ fontSize: 20, color: '#5fd4c4' }} />
                                    <Box>
                                        <Typography sx={{ fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                            Battery
                                        </Typography>
                                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)' }}>
                                            {config.storageKw} kW / {config.storageKwh} kWh
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {config.generatorKw > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <GeneratorIcon sx={{ fontSize: 20, color: '#ff9800' }} />
                                    <Box>
                                        <Typography sx={{ fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                            Generator
                                        </Typography>
                                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)' }}>
                                            {config.generatorKw.toLocaleString()} kW
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

/**
 * Main Modal Component
 */
export default function ScenarioCompareModalV2({ open, onClose, scenarios, projectName, projectId, onToggleView, embedded = false, allowedViews = null }) {
    const { isRetro } = useThemeMode();
    const { isMobile, isTablet } = useResponsive();
    const [activeSection, setActiveSection] = useState('overview');
    // Initialize viewMode to first allowed view, or 'basic' if not restricted
    const [viewMode, setViewMode] = useState(() => {
        if (allowedViews && allowedViews.length > 0) {
            return allowedViews[0];
        }
        return 'basic';
    }); // 'basic' or 'advanced'
    const [economicsSubtab, setEconomicsSubtab] = useState('developer'); // 'developer', 'customer', or 'cashflows'
    const [dispatchDayIndex, setDispatchDayIndex] = useState(null); // null = auto-select August peak
    const [dispatchChartsExpanded, setDispatchChartsExpanded] = useState(true); // collapsible dispatch section
    const [monthlyPerformanceData, setMonthlyPerformanceData] = useState({}); // { scenarioId: data }
    const [monthlyDataLoading, setMonthlyDataLoading] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);

    // Data extraction helpers (same as original)
    const getLatestRun = (scenario) => {
        const runs = scenario.optimization_runs || [];
        if (runs.length === 0) return null;
        // Filter out variant runs — they have different sizing configs and shouldn't
        // represent the scenario's base system configuration
        const baseRuns = runs.filter(r => r.run_type !== 'variant');
        const lastRunUuid = scenario.last_run_uuid;
        if (lastRunUuid) {
            const matchingRun = baseRuns.find(r => r.id === lastRunUuid || r.nrel_run_uuid === lastRunUuid);
            if (matchingRun) return matchingRun;
        }
        const completedRuns = baseRuns.filter(r => ['completed', 'optimal', 'Optimal Obtained'].includes(r.status));
        if (completedRuns.length > 0) {
            return completedRuns.sort((a, b) => {
                const aTime = a.completed_at ? new Date(a.completed_at) : new Date(0);
                const bTime = b.completed_at ? new Date(b.completed_at) : new Date(0);
                return bTime - aTime;
            })[0];
        }
        return baseRuns[0] || null;
    };

    const getOutputs = (scenario) => {
        const results = scenario.last_run_results || scenario.reopt_results || {};
        const outputs = results.outputs || {};
        return outputs.Scenario?.Site || outputs;
    };

    const getFinancialSummary = (scenario) => {
        // Primary: extract from solver optimization results (always current)
        const outputs = getOutputs(scenario);
        const solverFinancial = outputs.Financial || {};
        const solverTariff = outputs.ElectricTariff || {};

        // Fallback: saved financial summary from ui_state (may be stale)
        const saved = scenario.ui_state?.financialSummary;

        // Determine which source we're using for key metrics
        const hasSolverCapex = solverFinancial.initial_capital_costs != null;
        const hasSolverNpv = solverFinancial.npv != null;

        return {
            npv: solverFinancial.npv ?? saved?.npv ?? null,
            paybackPeriod: saved?.paybackPeriod ?? null, // solver doesn't compute simple payback directly
            totalCapex: hasSolverCapex
                ? (solverFinancial.initial_capital_costs_after_incentives ?? solverFinancial.initial_capital_costs)
                : (saved?.capex_breakdown?.net_capex ?? saved?.capex_breakdown?.total ?? saved?.initialInvestment ?? null),
            totalSavings: saved?.totalSavings ?? null,
            annualSavings: solverFinancial.year_one_bill_savings ?? saved?.annualSavings ?? null,
            year1NetSavings: saved?.year1NetSavings ?? null,
            bauYearOneUtilityCost: solverTariff.year_one_bill_before_tax ?? saved?.bauYearOneUtilityCost ?? null,
            // Flag when falling back to saved data so UI can indicate staleness
            _usingSavedData: !hasSolverCapex && !hasSolverNpv && saved != null,
            // VPP data
            vppAnnualRevenue: scenario.ui_state?.form?.dealAnalysisConfig?.computedResults?.vppAnnualRevenue ?? null,
            lifetimeVppRevenue: scenario.ui_state?.form?.dealAnalysisConfig?.computedResults?.lifetimeVppRevenue ?? null,
            vppIncluded: scenario.ui_state?.form?.dealAnalysisConfig?.vppIncluded ?? false,
        };
    };

    const getSystemConfig = (scenario) => {
        // Use solver outputs (actual optimized sizes) from the latest base run
        const existingSolarKw = parseFloat(scenario.ui_state?.loadProfile?.existingSolarParams?.system_capacity) || 0;
        const latestRun = getLatestRun(scenario);

        // First try solver-calculated values from optimization run
        if (latestRun) {
            return {
                pvKw: latestRun.pv_kw ?? 0,
                existingSolarKw,
                storageKw: latestRun.storage_kw ?? 0,
                storageKwh: latestRun.storage_kwh ?? 0,
                generatorKw: latestRun.generator_kw ?? 0,
            };
        }

        // Try REopt outputs
        const outputs = getOutputs(scenario);
        if (outputs.PV || outputs.ElectricStorage || outputs.Generator) {
            return {
                pvKw: outputs.PV?.size_kw ?? 0,
                existingSolarKw,
                storageKw: outputs.ElectricStorage?.size_kw ?? 0,
                storageKwh: outputs.ElectricStorage?.size_kwh ?? 0,
                generatorKw: outputs.Generator?.size_kw ?? 0,
            };
        }

        // Fall back to system_config_summary (scenarios with only variant runs)
        const summary = scenario.system_config_summary;
        if (summary) {
            return {
                pvKw: summary.pv_kw ?? 0,
                existingSolarKw,
                storageKw: summary.storage_kw ?? 0,
                storageKwh: summary.storage_kwh ?? 0,
                generatorKw: summary.generator_kw ?? 0,
            };
        }

        return { pvKw: 0, existingSolarKw, storageKw: 0, storageKwh: 0, generatorKw: 0 };
    };

    const getPerformanceData = (scenario) => {
        const outputs = getOutputs(scenario);
        const loadKwh = outputs.ElectricLoad?.annual_calculated_kwh ?? 0;
        const gridKwh = outputs.ElectricUtility?.annual_energy_supplied_kwh ?? loadKwh;

        // Peak load before (from load profile or REopt)
        const peakLoadBefore = outputs.ElectricLoad?.annual_calculated_peak_kw ??
                               outputs.ElectricLoad?.annual_peak_kw ?? null;

        // Peak load after - get from monthly performance data (max of post_der_peak_kw)
        const monthly = monthlyPerformanceData[scenario.id];
        const assetPerf = monthly?.asset_performance || monthly?.monthly_performance?.monthly_performance || [];
        let peakLoadAfter = null;
        if (assetPerf.length > 0) {
            peakLoadAfter = Math.max(...assetPerf.map(m => m.post_der_peak_kw || m.grid_peak_kw || 0));
            if (peakLoadAfter === 0) peakLoadAfter = null;
        }
        // Fallback to REopt outputs if monthly data not available
        if (peakLoadAfter === null) {
            peakLoadAfter = outputs.ElectricUtility?.annual_peak_kw ??
                            outputs.Site?.annual_peak_demand_kw ?? null;
        }

        // Generator run hours - calculate from hourly series if available
        let generatorRunHours = outputs.Generator?.annual_fuel_consumption_gal ?
            outputs.Generator?.annual_run_hours ?? null : null;
        // If no direct run_hours, estimate from hourly production
        if (generatorRunHours === null && outputs.Generator?.electric_to_load_series_kw) {
            const genSeries = outputs.Generator.electric_to_load_series_kw;
            generatorRunHours = genSeries.filter(v => v > 0.1).length; // Count hours with significant production
        }

        // Battery cycles - from storage outputs
        const batteryCycles = outputs.ElectricStorage?.year_one_battery_cycles ??
                              outputs.ElectricStorage?.annual_battery_cycles ?? null;

        return {
            pvProduction: outputs.PV?.year_one_energy_produced_kwh ?? outputs.PV?.annual_energy_produced_kwh ?? 0,
            loadKwh,
            gridKwh,
            gridReduction: loadKwh > 0 ? ((loadKwh - gridKwh) / loadKwh * 100) : 0,
            peakLoadBefore,
            peakLoadAfter,
            peakReduction: peakLoadBefore && peakLoadAfter ? ((peakLoadBefore - peakLoadAfter) / peakLoadBefore * 100) : null,
            generatorRunHours,
            batteryCycles,
        };
    };

    const getCashFlows = (scenario) => {
        // Use saved cash flows from post-processing
        const savedCashFlows = scenario.ui_state?.financialSummary?.cash_flows;
        return savedCashFlows && savedCashFlows.length > 0 ? savedCashFlows : [];
    };

    const getCapexBreakdown = (scenario) => {
        // Use saved capex breakdown from post-processing
        return scenario.ui_state?.financialSummary?.capex_breakdown ?? null;
    };

    const getOpexBreakdown = (scenario) => {
        // Use saved budget opex calculations
        return scenario.ui_state?.budget?.opexCalculations ?? null;
    };

    // Calculate scores based on saved financial data
    const scoredScenarios = useMemo(() => {
        if (!scenarios || scenarios.length === 0) return [];
        return calculateScenarioScores(scenarios, getFinancialSummary, getSystemConfig, getPerformanceData);
    }, [scenarios, monthlyPerformanceData]);

    const winnerIdx = scoredScenarios.length > 0
        ? scoredScenarios.reduce((maxIdx, s, idx, arr) => s.totalScore > arr[maxIdx].totalScore ? idx : maxIdx, 0)
        : -1;

    // Whether any compared scenario has solar PV, battery, or generator — used to hide irrelevant sections
    const anyScenarioHasPV = useMemo(() => scenarios.some(s => getSystemConfig(s).pvKw > 0), [scenarios]);
    const anyScenarioHasStorage = useMemo(() => scenarios.some(s => getSystemConfig(s).storageKw > 0), [scenarios]);
    const anyScenarioHasGenerator = useMemo(() => scenarios.some(s => getSystemConfig(s).generatorKw > 0), [scenarios]);
    const anyScenarioHasGeneration = anyScenarioHasPV || anyScenarioHasGenerator;

    // Format helpers (defined early for use in investmentQuality)
    const formatCurrency = (val, useParens = false) => {
        if (val == null || isNaN(val)) return '—';
        const absVal = Math.abs(val);
        const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(absVal);
        if (val < 0) {
            return useParens ? `(${formatted})` : `-${formatted}`;
        }
        return formatted;
    };

    const formatPayback = (val) => {
        if (val == null || isNaN(val)) return '—';
        if (val <= 0) return 'Immediate';
        return `${val.toFixed(1)} yrs`;
    };

    const formatPercent = (val) => {
        if (val == null || isNaN(val)) return '—';
        return `${val.toFixed(1)}%`;
    };

    // Analyze investment quality of the winner scenario
    const investmentQuality = useMemo(() => {
        if (winnerIdx < 0 || !scenarios[winnerIdx]) return { level: 'unknown', warnings: [] };

        const winner = scenarios[winnerIdx];
        const financial = getFinancialSummary(winner);
        const warnings = [];
        let level = 'good'; // 'good' | 'caution' | 'poor'

        // Check NPV
        if (financial.npv != null) {
            if (financial.npv < 0) {
                warnings.push({
                    type: 'npv_negative',
                    message: `Negative NPV (${formatCurrency(financial.npv)}) — project does not generate positive returns over analysis period`,
                    severity: 'high'
                });
                level = 'poor';
            } else if (financial.npv < 10000) {
                warnings.push({
                    type: 'npv_low',
                    message: 'Low NPV — marginal financial returns',
                    severity: 'medium'
                });
                if (level !== 'poor') level = 'caution';
            }
        }

        // Check payback period
        if (financial.paybackPeriod != null) {
            if (financial.paybackPeriod > 20) {
                warnings.push({
                    type: 'payback_long',
                    message: `Payback period (${financial.paybackPeriod.toFixed(1)} yrs) exceeds typical equipment lifespan`,
                    severity: 'high'
                });
                level = 'poor';
            } else if (financial.paybackPeriod > 12) {
                warnings.push({
                    type: 'payback_extended',
                    message: `Extended payback period (${financial.paybackPeriod.toFixed(1)} yrs)`,
                    severity: 'medium'
                });
                if (level !== 'poor') level = 'caution';
            }
        }

        // Check if savings are positive
        if (financial.annualSavings != null && financial.annualSavings <= 0) {
            warnings.push({
                type: 'no_savings',
                message: 'No positive annual savings projected',
                severity: 'high'
            });
            level = 'poor';
        }

        // Check if all scenarios have negative NPV
        const allNegativeNpv = scenarios.every(s => {
            const f = getFinancialSummary(s);
            return f.npv != null && f.npv < 0;
        });
        if (allNegativeNpv && scenarios.length > 1) {
            warnings.push({
                type: 'all_negative',
                message: 'All scenarios have negative NPV — consider revisiting assumptions or site conditions',
                severity: 'high'
            });
        }

        return { level, warnings };
    }, [winnerIdx, scenarios, getFinancialSummary]);

    // Calculate default dispatch day (peak day in August - day 213-243)
    const defaultDispatchDay = useMemo(() => {
        if (!scenarios || scenarios.length === 0) return 228; // Mid-August default

        // Use first scenario's load profile to find August peak
        const firstScenario = scenarios[0];
        const results = firstScenario?.last_run_results || firstScenario?.reopt_results;
        const load = results?.outputs?.ElectricLoad?.load_series_kw ||
                     results?.outputs?.Scenario?.Site?.ElectricLoad?.load_series_kw || [];

        if (load.length !== 8760) return 228; // Mid-August default

        // August is days 213-243 (0-indexed), hours 5112-5832
        let maxDailyAvg = -Infinity;
        let peakDay = 228;

        for (let day = 213; day <= 243; day++) {
            const startHour = day * 24;
            const dayLoad = load.slice(startHour, startHour + 24);
            const dayAvg = dayLoad.reduce((sum, v) => sum + (v || 0), 0) / 24;
            if (dayAvg > maxDailyAvg) {
                maxDailyAvg = dayAvg;
                peakDay = day;
            }
        }
        return peakDay;
    }, [scenarios]);

    // Initialize dispatch day to default on first render
    useEffect(() => {
        if (dispatchDayIndex === null && defaultDispatchDay) {
            setDispatchDayIndex(defaultDispatchDay);
        }
    }, [defaultDispatchDay, dispatchDayIndex]);

    // Fetch monthly performance data for all scenarios when modal opens (in parallel)
    // In embedded mode (shared pages), always fetch since there's no "open" state
    useEffect(() => {
        const shouldFetch = embedded ? true : open;
        if (!shouldFetch || !scenarios || scenarios.length === 0) return;

        const fetchAllMonthlyData = async () => {
            setMonthlyDataLoading(true);

            // Fetch all scenarios in parallel for faster loading
            const fetchPromises = scenarios.map(async (scenario) => {
                try {
                    const results = scenario.last_run_results || scenario.reopt_results;
                    if (!results?.outputs) return { id: scenario.id, data: null };

                    // Get tariff from scenario - check multiple locations for backwards compatibility
                    // Order: form state customTariffData > form state selectedTariffData > root tariff_data
                    const tariff = scenario.ui_state?.form?.tariff?.customTariffData ||
                                   scenario.ui_state?.form?.tariff?.selectedTariffData ||
                                   scenario.tariff_data;  // Fallback to root-level tariff_data
                    // caDerConfig is saved inside the tariff section of the form state
                    const caDerConfig = scenario.ui_state?.form?.tariff?.caDerConfig ||
                                        scenario.ui_state?.caDerConfig;

                    debug.log(`[Comparison] Scenario ${scenario.name} tariff lookup:`, {
                        hasFormCustomTariff: !!scenario.ui_state?.form?.tariff?.customTariffData,
                        hasFormSelectedTariff: !!scenario.ui_state?.form?.tariff?.selectedTariffData,
                        hasRootTariffData: !!scenario.tariff_data,
                        tariffFound: !!tariff,
                        tariffName: tariff?.name || tariff?.rate_schedule_name,
                        hasCaDerConfig: !!caDerConfig,
                        caDerGeneratorKw: caDerConfig?.generator_kw,
                        caDerApplyStandby: caDerConfig?.calculated?.final_standby_apply,
                    });

                    const data = await monthlyPerformanceService.fetchMonthlyPerformance(
                        results,
                        tariff,
                        { caDerConfig, forceRefresh: true }  // Force fresh data to avoid stale cache
                    );

                    // Debug: Log monthly data structure
                    debug.log(`[Comparison] Monthly data received for ${scenario.name}:`, {
                        hasBauCosts: data?.bau_monthly_costs?.length > 0,
                        bauCostsLength: data?.bau_monthly_costs?.length,
                        hasProjectCosts: data?.project_monthly_costs?.length > 0,
                        projectCostsLength: data?.project_monthly_costs?.length,
                        bauMonth1: data?.bau_monthly_costs?.[0],
                        projMonth1: data?.project_monthly_costs?.[0],
                        tariffSource: data?.tariff_source,
                    });

                    return { id: scenario.id, data };
                } catch (error) {
                    debug.error(`Failed to fetch monthly data for scenario ${scenario.id}:`, error);
                    return { id: scenario.id, data: null };
                }
            });

            const results = await Promise.all(fetchPromises);
            const dataMap = {};
            results.forEach(({ id, data }) => {
                if (data) dataMap[id] = data;
            });

            setMonthlyPerformanceData(dataMap);
            setMonthlyDataLoading(false);
        };

        fetchAllMonthlyData();
    }, [open, scenarios, embedded]);

    // Early return after all hooks
    if (!scenarios || scenarios.length === 0) return null;

    const anyScenarioHasVpp = scenarios.some(s => {
        const dealConfig = s.ui_state?.form?.dealAnalysisConfig;
        return dealConfig?.vppIncluded && dealConfig?.computedResults?.vppAnnualRevenue > 0;
    });

    const sections = [
        { id: 'overview', label: 'Overview' },
        { id: 'performance', label: 'Performance' },
        { id: 'economics', label: 'Economics' },
        ...(anyScenarioHasVpp ? [{ id: 'vpp', label: 'VPP Revenue' }] : []),
        { id: 'budget', label: 'Budget' },
    ];

    // Extract Deal Analysis results (IRR, ESA payments, customer economics)
    const getDealAnalysis = (scenario) => {
        // dealAnalysisConfig is saved inside form via scenarioForm.serializeFormState()
        const dealConfig = scenario.ui_state?.form?.dealAnalysisConfig;
        const computed = dealConfig?.computedResults;
        const esaConfig = dealConfig?.esaConfig;

        // Debug logging
        debug.log(`[COMPARE] getDealAnalysis for ${scenario.name}:`, {
            hasDealConfig: !!dealConfig,
            hasComputedResults: !!computed,
            hasEsaConfig: !!esaConfig,
            irrWithItc: computed?.irrWithItc,
            year1EsaPayment: computed?.year1EsaPayment,
        });

        // Compute Year 1 ESA Revenue from esaConfig if computedResults not available
        const computeYear1EsaRevenue = () => {
            if (computed?.year1EsaPayment) return computed.year1EsaPayment;
            if (!esaConfig) return null;

            // Get annual production from monthly performance data
            const monthly = monthlyPerformanceData[scenario.id];
            const assetPerf = monthly?.asset_performance || [];
            let annualProductionKwh = 0;
            assetPerf.forEach(m => {
                annualProductionKwh += (m.pv_to_load_kwh || 0) + (m.pv_to_battery_kwh || 0) + (m.pv_to_grid_kwh || 0);
            });

            // Get annual savings from financial summary
            const financial = getFinancialSummary(scenario);
            const annualSavings = financial?.annualSavings || 0;

            // Get system capacity
            const systemConfig = getSystemConfig(scenario);
            const totalCapacityKw = (systemConfig?.pvKw || 0) + (systemConfig?.storageKw || 0);

            // Calculate based on ESA model
            const model = esaConfig.model || 'fixed_rate';

            if (model === 'fixed_rate' && esaConfig.rate) {
                return annualProductionKwh * esaConfig.rate;
            } else if (model === 'savings_share' && esaConfig.sharePercent) {
                return annualSavings * esaConfig.sharePercent;
            } else if (model === 'fixed_monthly' && esaConfig.payment) {
                return esaConfig.payment * 12;
            } else if (model === 'capacity_payment' && esaConfig.ratePerKw) {
                return totalCapacityKw * esaConfig.ratePerKw * 12;
            }
            return null;
        };

        const year1EsaPayment = computeYear1EsaRevenue();

        // Compute effectiveRate and rateDiscount from available data
        // These are computed BEFORE the early return so customer economics
        // are available even without full deal analysis configuration
        // Effective Rate = (BAU cost - net savings) / kWh consumed
        // Rate Discount = Net savings / BAU cost
        const computeCustomerMetrics = () => {
            const saved = scenario.ui_state?.financialSummary;
            const loadProfile = scenario.ui_state?.loadProfile;

            const bauUtilityCost = saved?.bauYearOneUtilityCost || 0;

            // Compute NET load - this is what was sent to solver and what BAU is based on
            // See useLoadProfile.js for the same logic:
            //   NET = baseLoads + evLoads - existingSolarProfile (if baseLoadIsNetOfSolar is false)
            //   NET = baseLoads + evLoads (if baseLoadIsNetOfSolar is true, already net)
            const baseLoads = loadProfile?.baseLoads || [];
            const evLoads = loadProfile?.evLoads || [];
            const isEvEnabled = loadProfile?.isEvEnabled || false;
            const existingSolarProfile = loadProfile?.existingSolarProfile || [];
            const baseLoadIsNetOfSolar = loadProfile?.baseLoadIsNetOfSolar || false;

            let totalLoad = 0;
            if (baseLoads.length === 8760) {
                // Start with base loads
                totalLoad = baseLoads.reduce((sum, v) => sum + (v || 0), 0);

                // Add EV load if enabled
                if (isEvEnabled && evLoads.length === 8760) {
                    totalLoad += evLoads.reduce((sum, v) => sum + (v || 0), 0);
                }

                // Subtract existing solar ONLY if baseLoads is gross (not already net)
                if (!baseLoadIsNetOfSolar && existingSolarProfile.length === 8760) {
                    const existingSolarTotal = existingSolarProfile.reduce((sum, v) => sum + (v || 0), 0);
                    totalLoad -= existingSolarTotal;
                }
            }

            // Calculate CUSTOMER net savings (not developer net cash flow)
            // Customer Net Savings = Energy Savings - (Fuel + Standby + Departing Load + ESA Payment)
            const monthly = monthlyPerformanceData[scenario.id];
            let customerNetSavings = null;

            if (monthly) {
                const bauMonthlyCosts = monthly.bau_monthly_costs || [];
                const projectMonthlyCosts = monthly.project_monthly_costs || [];
                const derCharges = monthly.monthly_der_charges || [];
                const assetPerformance = monthly.asset_performance || [];

                // Energy + demand savings
                let energySavings = 0;
                let demandSavings = 0;
                for (let i = 0; i < 12; i++) {
                    const bau = bauMonthlyCosts.find(m => m.month === i + 1) || {};
                    const proj = projectMonthlyCosts.find(m => m.month === i + 1) || {};
                    energySavings += (bau.energy_cost || 0) - (proj.energy_cost || 0);
                    demandSavings += (bau.demand_cost || 0) - (proj.demand_cost || 0);
                }

                // Customer costs
                const fuelCosts = assetPerformance.reduce((sum, m) => sum + (m.generator_fuel_cost || 0), 0);
                const standbyCharges = derCharges.reduce((sum, m) => sum + (m.standby_cost || 0), 0);
                const departingLoadCharges = derCharges.reduce((sum, m) => sum + (m.departing_load_cost || 0), 0);
                const esaPayment = year1EsaPayment || 0;

                customerNetSavings = (energySavings + demandSavings) - fuelCosts - standbyCharges - departingLoadCharges - esaPayment;
            }

            // Debug logging for customer metrics computation
            debug.log(`[COMPARE] computeCustomerMetrics for ${scenario.name}:`, {
                bauUtilityCost,
                customerNetSavings,
                totalLoad,
                hasMonthlyData: !!monthly,
                baseLoadIsNetOfSolar,
                hasExistingSolar: existingSolarProfile.length === 8760,
            });

            let effectiveRate = null;
            let rateDiscount = null;
            let bauRate = null;

            // BAU Rate = BAU cost / kWh consumed
            if (totalLoad > 0 && bauUtilityCost > 0) {
                bauRate = bauUtilityCost / totalLoad;
            }

            // Effective Rate = (BAU cost - customer net savings) / kWh consumed
            if (totalLoad > 0 && bauUtilityCost > 0 && customerNetSavings !== null) {
                effectiveRate = (bauUtilityCost - customerNetSavings) / totalLoad;
            }

            // Rate Discount = Customer net savings / BAU cost (as percentage)
            if (bauUtilityCost > 0 && customerNetSavings !== null) {
                rateDiscount = customerNetSavings / bauUtilityCost;
            }

            return { effectiveRate, rateDiscount, bauRate };
        };

        const { effectiveRate, rateDiscount, bauRate } = computeCustomerMetrics();

        // Return customer economics even if no deal analysis is configured
        // This ensures Effective Rate and Rate Discount are always available
        if (!computed && !year1EsaPayment) {
            return {
                effectiveRate,
                rateDiscount,
                bauRate,
            };
        }

        return {
            // Developer economics - with ITC
            irrWithItc: computed?.irrWithItc ?? computed?.irr,
            developerNpvWithItc: computed?.developerNpvWithItc ?? computed?.developerNpv,
            paybackYearsWithItc: computed?.paybackYearsWithItc ?? computed?.paybackYears,
            // Developer economics - without ITC
            irrWithoutItc: computed?.irrWithoutItc,
            developerNpvWithoutItc: computed?.developerNpvWithoutItc,
            paybackYearsWithoutItc: computed?.paybackYearsWithoutItc,
            // CapEx and ITC
            totalCapex: computed?.totalCapex,
            itcAmount: computed?.itcAmount,
            netCapexAfterItc: computed?.netCapexAfterItc,
            // OpEx
            year1OpEx: computed?.year1OpEx,
            totalExpenses: computed?.totalExpenses,
            // Revenue
            totalRevenue: computed?.totalRevenue,
            year1EsaPayment,
            // Annual data for detailed view
            annualRevenue: computed?.annualRevenue,
            annualExpenses: computed?.annualExpenses,
            annualCashFlows: computed?.annualCashFlows,
            // Customer economics
            year1CustomerSavings: computed?.year1CustomerSavings,
            lifetimeCustomerSavings: computed?.lifetimeCustomerSavings,
            customerNpv: computed?.customerNpv,
            effectiveRate: effectiveRate,
            bauRate: bauRate,
            rateDiscount: rateDiscount,
            // Customer annual cash flows
            customerAnnualCashflows: computed?.customerAnnualCashflows,
            // ESA Configuration
            esaConfig,
            discountRate: dealConfig?.discountRate,
            // Legacy compatibility
            irr: computed?.irrWithItc ?? computed?.irr,
            developerNpv: computed?.developerNpvWithItc ?? computed?.developerNpv,
            paybackYears: computed?.paybackYearsWithItc ?? computed?.paybackYears,
        };
    };

    // Extract detailed savings breakdown from monthly performance data
    const getDetailedSavings = (scenario) => {
        const monthly = monthlyPerformanceData[scenario.id];
        if (!monthly) return null;

        const bauMonthlyCosts = monthly.bau_monthly_costs || [];
        const projectMonthlyCosts = monthly.project_monthly_costs || [];
        const derCharges = monthly.monthly_der_charges || [];
        const assetPerformance = monthly.asset_performance || [];

        // Calculate energy and demand savings by comparing BAU vs Project costs
        let energySavings = 0;
        let demandSavings = 0;
        for (let i = 0; i < 12; i++) {
            const bau = bauMonthlyCosts.find(m => m.month === i + 1) || {};
            const proj = projectMonthlyCosts.find(m => m.month === i + 1) || {};
            energySavings += (bau.energy_cost || 0) - (proj.energy_cost || 0);
            demandSavings += (bau.demand_cost || 0) - (proj.demand_cost || 0);
        }

        const standbyCharges = derCharges.reduce((sum, m) => sum + (m.standby_cost || 0), 0);
        const departingLoadCharges = derCharges.reduce((sum, m) => sum + (m.departing_load_cost || 0), 0);
        const fuelCosts = assetPerformance.reduce((sum, m) => sum + (m.generator_fuel_cost || 0), 0);
        const postProjectUtilityCosts = projectMonthlyCosts.reduce((sum, m) =>
            sum + (m.energy_cost || 0) + (m.demand_cost || 0), 0);

        return {
            energySavings,
            demandSavings,
            standbyCharges,
            departingLoadCharges,
            fuelCosts,
            postProjectUtilityCosts,
        };
    };

    // Extract monthly asset performance for charts
    const getMonthlyAssetPerformance = (scenario) => {
        const monthly = monthlyPerformanceData[scenario.id];
        if (!monthly) return [];
        return monthly.asset_performance || monthly.monthly_performance?.monthly_performance || [];
    };

    // Extract commercial assumptions from scenario
    const getCommercialAssumptions = (scenario) => {
        const assumptions = scenario.ui_state?.form?.commercialAssumptions || {};
        const dealConfig = scenario.ui_state?.form?.dealAnalysisConfig || {};
        const esaConfig = dealConfig?.esaConfig || {};
        return {
            analysisYears: assumptions.analysisYears || 25,
            discountRate: assumptions.discountRate || 8, // stored as percentage
            electricityEscalation: assumptions.electricityEscalation || 0.025,
            omEscalation: assumptions.omEscalation || 0.02,
            esaEscalation: esaConfig.escalation || 0.025, // ESA rate escalation
            // Also check deal analysis discount rate
            dealDiscountRate: dealConfig.discountRate || 0.08,
        };
    };

    // For embedded mode, render Dialog as static content (not modal) with contained height
    const dialogSx = embedded ? {
        position: 'static',
        '& .MuiDialog-container': {
            position: 'static',
            height: 'auto',
        },
        '& .MuiBackdrop-root': {
            display: 'none',
        },
    } : {};

    const paperSx = {
        width: embedded ? '100%' : '95vw',
        maxWidth: embedded ? 1400 : 1400,
        height: embedded ? (isMobile ? '85vh' : '90vh') : '90vh',
        maxHeight: embedded ? 'none' : 900,
        bgcolor: isRetro ? '#0d0d0d' : 'background.default',
        backgroundImage: isRetro ? 'radial-gradient(ellipse at top, rgba(178,140,80,0.03) 0%, transparent 50%)' : 'none',
        borderRadius: isMobile ? 1 : 2,
        border: '1px solid rgba(178,140,80,0.2)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...(embedded && {
            position: 'static',
            margin: '0 auto',
            boxShadow: 'none',
        }),
    };

    return (
        <>
        <Dialog
            open={embedded ? true : open}
            onClose={embedded ? undefined : onClose}
            maxWidth={false}
            fullWidth
            hideBackdrop={embedded}
            disableEscapeKeyDown={embedded}
            sx={dialogSx}
            PaperProps={{ sx: paperSx }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: isMobile ? 2 : 0,
                    borderBottom: '1px solid rgba(178,140,80,0.2)',
                    bgcolor: 'rgba(0,0,0,0.3)',
                    py: isMobile ? 1.5 : 2,
                    px: isMobile ? 2 : 3,
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontSize: isMobile ? '1.1rem' : '1.5rem',
                            fontWeight: 700,
                            fontFamily: vortxTypography.body,
                            color: '#b28c50',
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {embedded ? 'Project Proposal' : 'Scenario Comparison'}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: isMobile ? '0.7rem' : '0.8rem',
                            color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                            mt: 0.5,
                        }}
                    >
                        {embedded ? projectName : `${projectName} · ${scenarios.length} scenarios`}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
                    {/* Basic/Advanced Toggle - hidden when only one view is allowed */}
                    {(!allowedViews || allowedViews.length > 1) && (
                        <Box
                            sx={{
                                display: 'flex',
                                bgcolor: isRetro ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                borderRadius: 1,
                                p: 0.5,
                                border: isRetro ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                            }}
                        >
                            <Button
                                size="small"
                                startIcon={<BasicIcon sx={{ fontSize: 16 }} />}
                                onClick={() => setViewMode('basic')}
                                sx={{
                                    fontSize: '0.7rem',
                                    textTransform: 'none',
                                    px: 1.5,
                                    py: 0.5,
                                    minWidth: 'auto',
                                    color: viewMode === 'basic' ? '#b28c50' : isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                    bgcolor: viewMode === 'basic' ? 'rgba(178,140,80,0.15)' : 'transparent',
                                    borderRadius: 0.5,
                                    '&:hover': {
                                        bgcolor: viewMode === 'basic' ? 'rgba(178,140,80,0.2)' : isRetro ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                    },
                                }}
                            >
                                Basic
                            </Button>
                            <Button
                                size="small"
                                startIcon={<AdvancedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => setViewMode('advanced')}
                                sx={{
                                    fontSize: '0.7rem',
                                    textTransform: 'none',
                                    px: 1.5,
                                    py: 0.5,
                                    minWidth: 'auto',
                                    color: viewMode === 'advanced' ? '#b28c50' : isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                    bgcolor: viewMode === 'advanced' ? 'rgba(178,140,80,0.15)' : 'transparent',
                                    borderRadius: 0.5,
                                    '&:hover': {
                                        bgcolor: viewMode === 'advanced' ? 'rgba(178,140,80,0.2)' : isRetro ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                    },
                                }}
                            >
                                Advanced
                            </Button>
                        </Box>
                    )}
                    {onToggleView && (
                        <Button
                            size="small"
                            startIcon={<TableIcon />}
                            onClick={onToggleView}
                            sx={{
                                fontSize: '0.75rem',
                                textTransform: 'none',
                                color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                                border: isRetro ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)',
                                '&:hover': {
                                    bgcolor: isRetro ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                    borderColor: isRetro ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                },
                            }}
                        >
                            Classic View
                        </Button>
                    )}
                    {!embedded && (
                        <Tooltip title="Share this comparison">
                            <Button
                                size="small"
                                startIcon={<ShareIcon />}
                                onClick={() => setShareDialogOpen(true)}
                                sx={{
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                    color: '#b28c50',
                                    border: '1px solid rgba(178,140,80,0.3)',
                                    '&:hover': {
                                        bgcolor: 'rgba(178,140,80,0.1)',
                                        borderColor: 'rgba(178,140,80,0.5)',
                                    },
                                }}
                            >
                                Share
                            </Button>
                        </Tooltip>
                    )}
                    {!embedded && (
                        <IconButton onClick={onClose} sx={{ color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </Box>
            </DialogTitle>

            {/* Navigation - Only show in advanced mode */}
            {viewMode === 'advanced' && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        px: 3,
                        py: 1.5,
                        borderBottom: isRetro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                        bgcolor: 'rgba(0,0,0,0.2)',
                    }}
                >
                    {sections.map((section) => (
                        <Button
                            key={section.id}
                            onClick={() => setActiveSection(section.id)}
                            sx={{
                                px: 2.5,
                                py: 1,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                color: activeSection === section.id ? '#b28c50' : isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                bgcolor: activeSection === section.id ? 'rgba(178,140,80,0.1)' : 'transparent',
                                border: activeSection === section.id ? '1px solid rgba(178,140,80,0.3)' : '1px solid transparent',
                                borderRadius: 1,
                                '&:hover': {
                                    bgcolor: 'rgba(178,140,80,0.1)',
                                },
                            }}
                        >
                            {section.label}
                        </Button>
                    ))}
                </Box>
            )}

            {/* Content */}
            <DialogContent sx={{ p: isMobile ? 1.5 : 3, overflow: 'auto', flex: 1 }}>
                {/* BASIC VIEW */}
                {viewMode === 'basic' && (
                    <Box>
                        {/* Winner Banner */}
                        {winnerIdx >= 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexWrap: 'wrap',
                                    gap: isMobile ? 1 : 2,
                                    mb: isMobile ? 2 : 3,
                                    p: isMobile ? 1.5 : 2,
                                    borderRadius: isMobile ? 1 : 2,
                                    bgcolor: 'rgba(178,140,80,0.08)',
                                    border: '1px solid rgba(178,140,80,0.3)',
                                }}
                            >
                                <TrophyIcon sx={{ color: '#ffd700', fontSize: isMobile ? 24 : 28 }} />
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: isMobile ? '0.6rem' : '0.7rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                        Recommended
                                    </Typography>
                                    <Typography sx={{ fontSize: isMobile ? '0.95rem' : '1.1rem', fontWeight: 700, color: SCENARIO_COLORS[winnerIdx] }}>
                                        {scenarios[winnerIdx]?.name}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={`Score: ${scoredScenarios[winnerIdx]?.totalScore}`}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(178,140,80,0.2)',
                                        color: '#b28c50',
                                        fontWeight: 700,
                                        fontFamily: vortxTypography.mono,
                                        fontSize: isMobile ? '0.7rem' : '0.8rem',
                                    }}
                                />
                            </Box>
                        )}

                        {/* Quick Comparison Table */}
                        <Box sx={{ overflowX: 'auto', mx: isMobile ? -1.5 : 0, px: isMobile ? 1.5 : 0 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? 600 : 'auto' }}>
                                <thead>
                                    <tr>
                                        <th style={{
                                            textAlign: 'left',
                                            padding: '12px 16px',
                                            borderBottom: '2px solid #b28c50',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            width: 160,
                                        }}>
                                            Metric
                                        </th>
                                        {scenarios.map((s, idx) => (
                                            <th key={s.id} style={{
                                                textAlign: 'right',
                                                padding: '12px 16px',
                                                borderBottom: `2px solid ${SCENARIO_COLORS[idx]}`,
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                color: SCENARIO_COLORS[idx],
                                            }}>
                                                {s.name}
                                                {idx === winnerIdx && (
                                                    <TrophyIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', color: '#ffd700' }} />
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* NPV */}
                                    <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                        <td style={{ padding: '14px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', fontWeight: 500 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <TrendingUpIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                                Net Present Value
                                            </Box>
                                        </td>
                                        {scenarios.map((s, idx) => {
                                            const financial = getFinancialSummary(s);
                                            const isBest = idx === scoredScenarios.reduce((best, curr, i, arr) =>
                                                (curr.npv > arr[best].npv) ? i : best, 0);
                                            return (
                                                <td key={s.id} style={{
                                                    textAlign: 'right',
                                                    padding: '14px 16px',
                                                    fontSize: '0.95rem',
                                                    fontWeight: 600,
                                                    fontFamily: vortxTypography.mono,
                                                    color: isBest ? SCENARIO_COLORS[idx] : isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                                }}>
                                                    {formatCurrency(financial.npv)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* Payback Period */}
                                    <tr>
                                        <td style={{ padding: '14px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', fontWeight: 500 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <SpeedIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                                Payback Period
                                            </Box>
                                        </td>
                                        {scenarios.map((s, idx) => {
                                            const financial = getFinancialSummary(s);
                                            const validPaybacks = scoredScenarios.filter(sc => sc.payback > 0 && sc.payback < 999);
                                            const isBest = validPaybacks.length > 0 && idx === scoredScenarios.reduce((best, curr, i, arr) => {
                                                if (curr.payback <= 0 || curr.payback >= 999) return best;
                                                if (arr[best].payback <= 0 || arr[best].payback >= 999) return i;
                                                return (curr.payback < arr[best].payback) ? i : best;
                                            }, 0);
                                            return (
                                                <td key={s.id} style={{
                                                    textAlign: 'right',
                                                    padding: '14px 16px',
                                                    fontSize: '0.95rem',
                                                    fontWeight: 600,
                                                    fontFamily: vortxTypography.mono,
                                                    color: isBest ? SCENARIO_COLORS[idx] : isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                                }}>
                                                    {formatPayback(financial.paybackPeriod)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* Total CapEx */}
                                    <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                        <td style={{ padding: '14px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', fontWeight: 500 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CapexIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                                Total CapEx (Net)
                                            </Box>
                                        </td>
                                        {scenarios.map((s, idx) => {
                                            const financial = getFinancialSummary(s);
                                            return (
                                                <td key={s.id} style={{
                                                    textAlign: 'right',
                                                    padding: '14px 16px',
                                                    fontSize: '0.95rem',
                                                    fontWeight: 600,
                                                    fontFamily: vortxTypography.mono,
                                                    color: financial._usingSavedData ? 'rgba(255,200,100,0.7)' : isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                                }}>
                                                    {financial._usingSavedData ? (
                                                        <Tooltip title="Estimated — open scenario results to refresh" arrow>
                                                            <span>{formatCurrency(financial.totalCapex)}*</span>
                                                        </Tooltip>
                                                    ) : (
                                                        formatCurrency(financial.totalCapex)
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* Year 1 Savings */}
                                    <tr>
                                        <td style={{ padding: '14px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', fontWeight: 500 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <TrendingUpIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                                Year 1 Net Savings
                                            </Box>
                                        </td>
                                        {scenarios.map((s, idx) => {
                                            const financial = getFinancialSummary(s);
                                            return (
                                                <td key={s.id} style={{
                                                    textAlign: 'right',
                                                    padding: '14px 16px',
                                                    fontSize: '0.95rem',
                                                    fontWeight: 600,
                                                    fontFamily: vortxTypography.mono,
                                                    color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                                }}>
                                                    {formatCurrency(financial.year1NetSavings)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* VPP Annual Revenue - only if any scenario has VPP */}
                                    {scenarios.some(s => {
                                        const f = getFinancialSummary(s);
                                        return f.vppIncluded && f.vppAnnualRevenue > 0;
                                    }) && (
                                        <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                            <td style={{ padding: '14px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', fontWeight: 500 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <AttachMoneyIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                                    VPP Revenue (Annual)
                                                </Box>
                                            </td>
                                            {scenarios.map(s => {
                                                const financial = getFinancialSummary(s);
                                                const hasVpp = financial.vppIncluded && financial.vppAnnualRevenue > 0;
                                                return (
                                                    <td key={s.id} style={{
                                                        textAlign: 'right',
                                                        padding: '14px 16px',
                                                        fontSize: '0.95rem',
                                                        fontWeight: 600,
                                                        fontFamily: vortxTypography.mono,
                                                        color: hasVpp ? 'rgba(255,255,255,0.8)' : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                    }}>
                                                        {hasVpp ? formatCurrency(financial.vppAnnualRevenue) : '—'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )}
                                    {/* Lifetime VPP Revenue - only if any scenario has VPP */}
                                    {scenarios.some(s => {
                                        const f = getFinancialSummary(s);
                                        return f.vppIncluded && f.lifetimeVppRevenue > 0;
                                    }) && (
                                        <tr>
                                            <td style={{ padding: '14px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', fontWeight: 500 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <AttachMoneyIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                                    Lifetime VPP Revenue
                                                </Box>
                                            </td>
                                            {scenarios.map(s => {
                                                const financial = getFinancialSummary(s);
                                                const hasVpp = financial.vppIncluded && financial.lifetimeVppRevenue > 0;
                                                return (
                                                    <td key={s.id} style={{
                                                        textAlign: 'right',
                                                        padding: '14px 16px',
                                                        fontSize: '0.95rem',
                                                        fontWeight: 600,
                                                        fontFamily: vortxTypography.mono,
                                                        color: hasVpp ? 'rgba(255,255,255,0.8)' : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                    }}>
                                                        {hasVpp ? formatCurrency(financial.lifetimeVppRevenue) : '—'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )}
                                    {/* Section: System Configuration */}
                                    <tr>
                                        <td colSpan={scenarios.length + 1} style={{
                                            padding: '20px 16px 10px',
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            color: '#5fd4c4',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.1em',
                                            borderTop: isRetro ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                                        }}>
                                            System Configuration
                                        </td>
                                    </tr>
                                    {/* Solar - only show if any scenario has PV */}
                                    {scenarios.some(s => getSystemConfig(s).pvKw > 0) && (
                                        <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                            <td style={{ padding: '12px 16px 12px 32px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <SolarIcon sx={{ fontSize: 16, color: '#ffd700' }} />
                                                    Solar PV
                                                </Box>
                                            </td>
                                            {scenarios.map(s => {
                                                const config = getSystemConfig(s);
                                                return (
                                                    <td key={s.id} style={{
                                                        textAlign: 'right',
                                                        padding: '12px 16px',
                                                        fontSize: '0.9rem',
                                                        fontFamily: vortxTypography.mono,
                                                        color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                                    }}>
                                                        {config.pvKw > 0 ? `${config.pvKw.toLocaleString()} kW` : '—'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )}
                                    {/* Battery - only show if any scenario has battery */}
                                    {anyScenarioHasStorage && (
                                        <tr>
                                            <td style={{ padding: '12px 16px 12px 32px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <BatteryIcon sx={{ fontSize: 16, color: '#5fd4c4' }} />
                                                    Battery Storage
                                                </Box>
                                            </td>
                                            {scenarios.map(s => {
                                                const config = getSystemConfig(s);
                                                return (
                                                    <td key={s.id} style={{
                                                        textAlign: 'right',
                                                        padding: '12px 16px',
                                                        fontSize: '0.9rem',
                                                        fontFamily: vortxTypography.mono,
                                                        color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                                    }}>
                                                        {config.storageKw > 0 ? `${config.storageKw} kW / ${config.storageKwh} kWh` : '—'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )}
                                    {/* Generator - only show if any scenario has one */}
                                    {scenarios.some(s => getSystemConfig(s).generatorKw > 0) && (
                                        <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                            <td style={{ padding: '12px 16px 12px 32px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <GeneratorIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                                                    Generator
                                                </Box>
                                            </td>
                                            {scenarios.map(s => {
                                                const config = getSystemConfig(s);
                                                return (
                                                    <td key={s.id} style={{
                                                        textAlign: 'right',
                                                        padding: '12px 16px',
                                                        fontSize: '0.9rem',
                                                        fontFamily: vortxTypography.mono,
                                                        color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                                    }}>
                                                        {config.generatorKw > 0 ? `${config.generatorKw.toLocaleString()} kW` : '—'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </Box>

                        {/* Investment Quality Warnings in Basic View */}
                        {investmentQuality.warnings.length > 0 && (
                            <Box
                                sx={{
                                    mt: 3,
                                    p: 2,
                                    borderRadius: 1,
                                    bgcolor: investmentQuality.level === 'poor'
                                        ? 'rgba(239,68,68,0.08)'
                                        : 'rgba(255,180,0,0.08)',
                                    border: investmentQuality.level === 'poor'
                                        ? '1px solid rgba(239,68,68,0.25)'
                                        : '1px solid rgba(255,180,0,0.25)',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <WarningIcon sx={{ fontSize: 18, color: investmentQuality.level === 'poor' ? '#ef4444' : '#ffb400' }} />
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: investmentQuality.level === 'poor' ? '#ef4444' : '#ffb400' }}>
                                        {investmentQuality.level === 'poor' ? 'Investment Concerns' : 'Advisory Notes'}
                                    </Typography>
                                </Box>
                                {investmentQuality.warnings.map((warning, idx) => (
                                    <Typography key={idx} sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', pl: 3.5 }}>
                                        • {warning.message}
                                    </Typography>
                                ))}
                            </Box>
                        )}

                        {/* Switch to Advanced Hint */}
                        <Box sx={{ mt: 4, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>
                                Switch to <strong>Advanced</strong> view for detailed charts, cash flow projections, and financial assumptions
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* ADVANCED VIEW */}
                {viewMode === 'advanced' && activeSection === 'overview' && (
                    <Box>
                        {/* Score Cards */}
                        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', mb: 4, flexWrap: 'wrap' }}>
                            {scoredScenarios.map((s, idx) => (
                                <Box
                                    key={s.id}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 2,
                                        p: 3,
                                        borderRadius: 2,
                                        bgcolor: idx === winnerIdx ? 'rgba(178,140,80,0.08)' : isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                                        border: `1px solid ${idx === winnerIdx ? 'rgba(178,140,80,0.4)' : isRetro ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    <ScoreRing
                                        score={s.totalScore}
                                        color={SCENARIO_COLORS[idx]}
                                        isWinner={idx === winnerIdx}
                                        size={140}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            color: SCENARIO_COLORS[idx],
                                            textAlign: 'center',
                                        }}
                                    >
                                        {s.name}
                                    </Typography>
                                    {idx === winnerIdx && (
                                        <Chip
                                            icon={investmentQuality.level === 'good'
                                                ? <TrophyIcon sx={{ fontSize: 16 }} />
                                                : investmentQuality.level === 'caution'
                                                    ? <InfoIcon sx={{ fontSize: 16 }} />
                                                    : <WarningIcon sx={{ fontSize: 16 }} />
                                            }
                                            label={investmentQuality.level === 'good'
                                                ? 'RECOMMENDED'
                                                : investmentQuality.level === 'caution'
                                                    ? 'BEST OPTION'
                                                    : 'BEST OF POOR OPTIONS'
                                            }
                                            size="small"
                                            sx={{
                                                bgcolor: investmentQuality.level === 'good'
                                                    ? 'rgba(255,215,0,0.15)'
                                                    : investmentQuality.level === 'caution'
                                                        ? 'rgba(255,180,0,0.15)'
                                                        : 'rgba(239,68,68,0.15)',
                                                color: investmentQuality.level === 'good'
                                                    ? '#ffd700'
                                                    : investmentQuality.level === 'caution'
                                                        ? '#ffb400'
                                                        : '#ef4444',
                                                border: investmentQuality.level === 'good'
                                                    ? '1px solid rgba(255,215,0,0.3)'
                                                    : investmentQuality.level === 'caution'
                                                        ? '1px solid rgba(255,180,0,0.3)'
                                                        : '1px solid rgba(239,68,68,0.3)',
                                                fontWeight: 700,
                                                fontSize: '0.6rem',
                                                letterSpacing: '0.05em',
                                            }}
                                        />
                                    )}
                                </Box>
                            ))}
                        </Box>

                        {/* Investment Quality Warnings */}
                        {investmentQuality.warnings.length > 0 && (
                            <Box
                                sx={{
                                    mb: 4,
                                    p: 2.5,
                                    borderRadius: 2,
                                    bgcolor: investmentQuality.level === 'poor'
                                        ? 'rgba(239,68,68,0.08)'
                                        : 'rgba(255,180,0,0.08)',
                                    border: investmentQuality.level === 'poor'
                                        ? '1px solid rgba(239,68,68,0.25)'
                                        : '1px solid rgba(255,180,0,0.25)',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <WarningIcon
                                        sx={{
                                            fontSize: 20,
                                            color: investmentQuality.level === 'poor' ? '#ef4444' : '#ffb400',
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '0.8rem',
                                            fontWeight: 700,
                                            color: investmentQuality.level === 'poor' ? '#ef4444' : '#ffb400',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                        }}
                                    >
                                        {investmentQuality.level === 'poor'
                                            ? 'Investment Concerns'
                                            : 'Advisory Notes'
                                        }
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {investmentQuality.warnings.map((warning, idx) => (
                                        <Typography
                                            key={idx}
                                            sx={{
                                                fontSize: '0.8rem',
                                                color: isRetro ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.75)',
                                                lineHeight: 1.5,
                                                pl: 3.5,
                                                position: 'relative',
                                                '&::before': {
                                                    content: '"•"',
                                                    position: 'absolute',
                                                    left: 12,
                                                    color: warning.severity === 'high' ? '#ef4444' : '#ffb400',
                                                },
                                            }}
                                        >
                                            {warning.message}
                                        </Typography>
                                    ))}
                                </Box>
                                {investmentQuality.level === 'poor' && (
                                    <Typography
                                        sx={{
                                            fontSize: '0.75rem',
                                            color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                            mt: 2,
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        Scores above reflect relative ranking only — a higher score does not indicate a good investment.
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {/* Radar Chart */}
                        <Box sx={{ mb: 4 }}>
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                    letterSpacing: '0.1em',
                                    textTransform: 'uppercase',
                                    mb: 2,
                                    textAlign: 'center',
                                }}
                            >
                                Multi-Dimensional Comparison
                            </Typography>
                            <ComparisonRadar scoredScenarios={scoredScenarios} />
                        </Box>
                    </Box>
                )}

                {/* PERFORMANCE TAB */}
                {viewMode === 'advanced' && activeSection === 'performance' && (
                    <Box>
                        {/* Asset Configuration Section */}
                        <Typography
                            sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                mb: 2,
                            }}
                        >
                            Asset Configuration
                        </Typography>
                        <Box sx={{ overflowX: 'auto', mb: 4 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{
                                            textAlign: 'left',
                                            padding: '12px 16px',
                                            borderBottom: '2px solid #b28c50',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                                            textTransform: 'uppercase',
                                        }}>
                                            Asset
                                        </th>
                                        {scenarios.map((s, idx) => (
                                            <th key={s.id} style={{
                                                textAlign: 'right',
                                                padding: '12px 16px',
                                                borderBottom: `2px solid ${SCENARIO_COLORS[idx]}`,
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                color: SCENARIO_COLORS[idx],
                                            }}>
                                                {s.name}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Solar PV - only show if any scenario has PV */}
                                    {scenarios.some(s => getSystemConfig(s).pvKw > 0) && (
                                        <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                            <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <SolarIcon sx={{ fontSize: 18, color: '#ffd700' }} />
                                                    Solar PV (kW)
                                                </Box>
                                            </td>
                                            {scenarios.map(s => {
                                                const config = getSystemConfig(s);
                                                return (
                                                    <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.9rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                        {config.pvKw > 0 ? config.pvKw.toLocaleString() : '—'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )}
                                    {/* Existing Solar */}
                                    {scenarios.some(s => getSystemConfig(s).existingSolarKw > 0) && (
                                        <tr>
                                            <td style={{ padding: '12px 16px 12px 40px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                                                Existing Solar (kW)
                                            </td>
                                            {scenarios.map(s => {
                                                const config = getSystemConfig(s);
                                                return (
                                                    <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.9rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                                                        {config.existingSolarKw > 0 ? config.existingSolarKw.toLocaleString() : '—'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )}
                                    {/* Battery Power - only show if any scenario has battery */}
                                    {anyScenarioHasStorage && (
                                        <>
                                            <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <BatteryIcon sx={{ fontSize: 18, color: '#5fd4c4' }} />
                                                        Battery Power (kW)
                                                    </Box>
                                                </td>
                                                {scenarios.map(s => {
                                                    const config = getSystemConfig(s);
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.9rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                            {config.storageKw > 0 ? config.storageKw.toLocaleString() : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* Battery Capacity */}
                                            <tr>
                                                <td style={{ padding: '12px 16px 12px 40px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                                                    Battery Capacity (kWh)
                                                </td>
                                                {scenarios.map(s => {
                                                    const config = getSystemConfig(s);
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.9rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                                                            {config.storageKwh > 0 ? config.storageKwh.toLocaleString() : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        </>
                                    )}
                                    {/* Generator */}
                                    {scenarios.some(s => getSystemConfig(s).generatorKw > 0) && (
                                        <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                            <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <GeneratorIcon sx={{ fontSize: 18, color: '#ff9800' }} />
                                                    Generator (kW)
                                                </Box>
                                            </td>
                                            {scenarios.map(s => {
                                                const config = getSystemConfig(s);
                                                return (
                                                    <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.9rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                        {config.generatorKw > 0 ? config.generatorKw.toLocaleString() : '—'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </Box>

                        {/* Energy Metrics Section */}
                        <Typography
                            sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                mb: 2,
                            }}
                        >
                            Energy Metrics
                        </Typography>
                        <Box sx={{ overflowX: 'auto', mb: 4 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{
                                            textAlign: 'left',
                                            padding: '12px 16px',
                                            borderBottom: '2px solid #b28c50',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                                            textTransform: 'uppercase',
                                        }}>
                                            Metric
                                        </th>
                                        {scenarios.map((s, idx) => (
                                            <th key={s.id} style={{
                                                textAlign: 'right',
                                                padding: '12px 16px',
                                                borderBottom: `2px solid ${SCENARIO_COLORS[idx]}`,
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                color: SCENARIO_COLORS[idx],
                                            }}>
                                                {s.name}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Annual PV Production - only show if any scenario has PV */}
                                    {anyScenarioHasPV && (
                                        <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                            <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                Annual PV Production (kWh)
                                            </td>
                                            {scenarios.map(s => {
                                                const perf = getPerformanceData(s);
                                                return (
                                                    <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.9rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                        {perf.pvProduction > 0 ? perf.pvProduction.toLocaleString() : '—'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )}
                                    {/* Annual Load */}
                                    <tr>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                            Annual Load (kWh)
                                        </td>
                                        {scenarios.map(s => {
                                            const perf = getPerformanceData(s);
                                            return (
                                                <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.9rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    {perf.loadKwh > 0 ? perf.loadKwh.toLocaleString() : '—'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* Grid Consumption */}
                                    <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                            Grid Consumption (kWh)
                                        </td>
                                        {scenarios.map(s => {
                                            const perf = getPerformanceData(s);
                                            return (
                                                <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.9rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    {perf.gridKwh > 0 ? perf.gridKwh.toLocaleString() : '—'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* Grid Reduction */}
                                    <tr>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                            Grid Reduction (%)
                                        </td>
                                        {scenarios.map((s, idx) => {
                                            const perf = getPerformanceData(s);
                                            const isBest = scoredScenarios.length > 0 &&
                                                idx === scoredScenarios.reduce((best, curr, i, arr) =>
                                                    (curr.gridReduction > arr[best].gridReduction) ? i : best, 0);
                                            return (
                                                <td key={s.id} style={{
                                                    textAlign: 'right',
                                                    padding: '12px 16px',
                                                    fontSize: '0.9rem',
                                                    fontFamily: vortxTypography.mono,
                                                    color: isBest ? '#22c55e' : isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                                    fontWeight: isBest ? 700 : 400,
                                                }}>
                                                    {formatPercent(perf.gridReduction)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* Peak Load Before */}
                                    <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                            Peak Load - Before (kW)
                                        </td>
                                        {scenarios.map(s => {
                                            const perf = getPerformanceData(s);
                                            return (
                                                <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.9rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    {perf.peakLoadBefore ? perf.peakLoadBefore.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* Peak Load After */}
                                    <tr>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                            Peak Load - After (kW)
                                        </td>
                                        {scenarios.map((s, idx) => {
                                            const perf = getPerformanceData(s);
                                            const isBest = scenarios.length > 1 && perf.peakLoadAfter &&
                                                idx === scenarios.reduce((best, curr, i) => {
                                                    const bestPeakAfter = getPerformanceData(scenarios[best]).peakLoadAfter;
                                                    const currPeakAfter = getPerformanceData(curr).peakLoadAfter;
                                                    if (!currPeakAfter) return best;
                                                    if (!bestPeakAfter) return i;
                                                    return currPeakAfter < bestPeakAfter ? i : best;
                                                }, 0);
                                            return (
                                                <td key={s.id} style={{
                                                    textAlign: 'right',
                                                    padding: '12px 16px',
                                                    fontSize: '0.9rem',
                                                    fontFamily: vortxTypography.mono,
                                                    color: isBest ? '#22c55e' : isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                                    fontWeight: isBest ? 700 : 400,
                                                }}>
                                                    {perf.peakLoadAfter ? perf.peakLoadAfter.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* Peak Reduction */}
                                    <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                            Peak Reduction (%)
                                        </td>
                                        {scenarios.map((s, idx) => {
                                            const perf = getPerformanceData(s);
                                            const isBest = scenarios.length > 1 && perf.peakReduction &&
                                                idx === scenarios.reduce((best, curr, i) => {
                                                    const bestRed = getPerformanceData(scenarios[best]).peakReduction;
                                                    const currRed = getPerformanceData(curr).peakReduction;
                                                    if (!currRed) return best;
                                                    if (!bestRed) return i;
                                                    return currRed > bestRed ? i : best;
                                                }, 0);
                                            return (
                                                <td key={s.id} style={{
                                                    textAlign: 'right',
                                                    padding: '12px 16px',
                                                    fontSize: '0.9rem',
                                                    fontFamily: vortxTypography.mono,
                                                    color: isBest ? '#22c55e' : isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                                    fontWeight: isBest ? 700 : 400,
                                                }}>
                                                    {perf.peakReduction ? formatPercent(perf.peakReduction) : '—'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* Battery Cycles */}
                                    {scenarios.some(s => getPerformanceData(s).batteryCycles > 0) && (
                                        <tr>
                                            <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                Battery Cycles (annual)
                                            </td>
                                            {scenarios.map(s => {
                                                const perf = getPerformanceData(s);
                                                return (
                                                    <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.9rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                        {perf.batteryCycles ? perf.batteryCycles.toFixed(0) : '—'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )}
                                    {/* Generator Run Hours */}
                                    {scenarios.some(s => getPerformanceData(s).generatorRunHours > 0) && (
                                        <tr>
                                            <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                Generator Run Hours (annual)
                                            </td>
                                            {scenarios.map(s => {
                                                const perf = getPerformanceData(s);
                                                return (
                                                    <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.9rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                        {perf.generatorRunHours ? perf.generatorRunHours.toLocaleString() : '—'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </Box>

                        {/* 24-Hour Dispatch Section - Collapsible */}
                        <Box
                            onClick={() => setDispatchChartsExpanded(!dispatchChartsExpanded)}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: dispatchChartsExpanded ? 2 : 0,
                                cursor: 'pointer',
                                py: 1,
                                px: 1,
                                mx: -1,
                                borderRadius: 1,
                                '&:hover': { bgcolor: isRetro ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {dispatchChartsExpanded ? (
                                    <ExpandLessIcon sx={{ fontSize: '1.2rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }} />
                                ) : (
                                    <ExpandMoreIcon sx={{ fontSize: '1.2rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }} />
                                )}
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    24-Hour Dispatch (Day {(dispatchDayIndex || defaultDispatchDay) + 1})
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                                <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>Day:</Typography>
                                <TextField
                                    type="number"
                                    size="small"
                                    value={dispatchDayIndex ?? defaultDispatchDay}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value, 10);
                                        if (val >= 1 && val <= 365) {
                                            setDispatchDayIndex(val - 1);
                                        }
                                    }}
                                    inputProps={{ min: 1, max: 365 }}
                                    sx={{
                                        width: 80,
                                        '& .MuiInputBase-input': {
                                            fontSize: '0.8rem',
                                            padding: '6px 8px',
                                            color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: isRetro ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)' },
                                            '&:hover fieldset': { borderColor: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' },
                                        },
                                    }}
                                />
                            </Box>
                        </Box>

                        <Collapse in={dispatchChartsExpanded}>
                            {/* Dispatch Charts - Stacked vertically for full width */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                                {scenarios.map((scenario, idx) => {
                                    const results = scenario.last_run_results || scenario.reopt_results;
                                    const { data: rawDispatchData } = reoptToDailySeries(results, dispatchDayIndex ?? defaultDispatchDay);

                                    // Transform data to match ScenarioEditor's normalGraphData format
                                    const dispatchData = rawDispatchData.map(d => ({
                                        ...d,
                                        // Aggregated fields expected by PowerGraph
                                        utility: (d.utility_to_load || 0) + (d.utility_to_bess || 0),
                                        solar: (d.solar_to_load || 0) + (d.solar_to_bess || 0),
                                        bess: d.bess_to_load || 0,
                                        net_utility: ((d.utility_to_load || 0) + (d.utility_to_bess || 0)) -
                                            ((d.solar_export || 0) + (d.bess_export || 0) + (d.diesel_export || 0)),
                                        genset: (d.diesel_to_load || 0) + (d.ng_to_load || 0)
                                    }));

                                    return (
                                        <Box
                                            key={scenario.id}
                                            sx={{
                                                width: '100%',
                                                p: 1,
                                                borderRadius: 1,
                                                bgcolor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                                                border: `1px solid ${SCENARIO_COLORS[idx]}33`,
                                            }}
                                        >
                                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: SCENARIO_COLORS[idx], mb: 0.5, ml: 1 }}>
                                                {scenario.name}
                                            </Typography>
                                            <Box sx={{
                                                height: 130,
                                                overflow: 'hidden',
                                                '& > div': { height: '180px !important', mt: '-35px' },
                                                '& > div > div:first-of-type': { display: 'none' },
                                                '& > div > div:last-of-type': { height: '180px !important' },
                                                '& > div > div:last-of-type > div:last-of-type': { display: 'none' },
                                            }}>
                                                <PowerGraph
                                                    data={dispatchData}
                                                    isDark={true}
                                                    showControls={false}
                                                />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>

                            {/* Shared Dispatch Legend - matches PowerGraph exactly */}
                            <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                gap: 2,
                                mb: 4,
                                py: 1.5,
                                px: 2,
                                borderRadius: 1,
                                bgcolor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                            }}>
                                {/* Facility Load - charcoal dashed line */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Box sx={{ width: 20, height: 0, borderTop: `3px dashed ${vortxColors.charcoal}` }} />
                                    <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>Facility Load</Typography>
                                </Box>
                                {/* Net Utility Load - gold dashed */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Box sx={{ width: 20, height: 0, borderTop: `3px dashed ${vortxColors.gold}` }} />
                                    <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>Net Utility Load</Typography>
                                </Box>
                                {/* Grid Serves Load - navy blue */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Box sx={{ width: 14, height: 10, bgcolor: '#1A3A5C', borderRadius: 0.5 }} />
                                    <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>Grid Serves Load</Typography>
                                </Box>
                                {/* PV Serves Load - bright green */}
                                {anyScenarioHasPV && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Box sx={{ width: 14, height: 10, bgcolor: '#6BBF59', borderRadius: 0.5 }} />
                                        <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>PV Serves Load</Typography>
                                    </Box>
                                )}
                                {/* BESS Serves Load - gray with diagonal hatching */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Box sx={{
                                        width: 14,
                                        height: 10,
                                        bgcolor: '#888888',
                                        borderRadius: 0.5,
                                        backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.6) 0 2px, transparent 2px 5px)',
                                    }} />
                                    <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>BESS Serves Load</Typography>
                                </Box>
                                {/* Generator Serves Load - forest green */}
                                {anyScenarioHasGenerator && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Box sx={{ width: 14, height: 10, bgcolor: '#2E7D32', borderRadius: 0.5 }} />
                                        <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>Generator Serves Load</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Collapse>

                        {/* Monthly Production Chart - only show if any scenario has generation */}
                        {anyScenarioHasGeneration && (<>
                        <Typography
                            sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                mt: 4,
                                mb: 2,
                            }}
                        >
                            Monthly Energy Production
                        </Typography>
                        {monthlyDataLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={32} sx={{ color: '#b28c50' }} />
                            </Box>
                        ) : (
                            <Box sx={{ height: 250 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={(() => {
                                            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                            return months.map((month, monthIdx) => {
                                                const row = { month };
                                                scenarios.forEach((s, sIdx) => {
                                                    const monthly = getMonthlyAssetPerformance(s);
                                                    const monthData = monthly[monthIdx];
                                                    const pvProduction = monthData?.pv_production_kwh || monthData?.pv_to_load_kwh || 0;
                                                    const genProduction = monthData?.generator_to_load_kwh || 0;
                                                    row[`scenario${sIdx}`] = pvProduction + genProduction;
                                                });
                                                return row;
                                            });
                                        })()}
                                        margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke={isRetro ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"} />
                                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }} />
                                        <YAxis tick={{ fontSize: 11, fill: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                                        <RechartsTooltip
                                            contentStyle={{ backgroundColor: isRetro ? '#1a1a1a' : '#fff', border: isRetro ? '1px solid #333' : '1px solid #ddd', fontSize: '0.75rem', color: isRetro ? '#fff' : '#2c2c2c' }}
                                            formatter={(value) => `${value.toLocaleString()} kWh`}
                                        />
                                        <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                                        {scenarios.map((s, idx) => (
                                            <Bar
                                                key={s.id}
                                                dataKey={`scenario${idx}`}
                                                name={s.name}
                                                fill={SCENARIO_COLORS[idx]}
                                                opacity={0.8}
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        )}
                        </>)}
                    </Box>
                )}

                {/* ECONOMICS TAB */}
                {viewMode === 'advanced' && activeSection === 'economics' && (
                    embedded ? (
                        <InteractiveEconomicsPanel
                            scenarios={scenarios}
                            getSystemConfig={getSystemConfig}
                            getFinancialSummary={getFinancialSummary}
                            getDealAnalysis={getDealAnalysis}
                            getCapexBreakdown={getCapexBreakdown}
                            getOpexBreakdown={getOpexBreakdown}
                            getCashFlows={getCashFlows}
                            getDetailedSavings={getDetailedSavings}
                            getCommercialAssumptions={getCommercialAssumptions}
                            monthlyPerformanceData={monthlyPerformanceData}
                            economicsSubtab={economicsSubtab}
                            setEconomicsSubtab={setEconomicsSubtab}
                        />
                    ) : (
                    <Box>
                        {/* Economics Subtabs */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                            <Button
                                onClick={() => setEconomicsSubtab('developer')}
                                sx={{
                                    px: 2,
                                    py: 0.75,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    color: economicsSubtab === 'developer' ? '#b28c50' : isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                    bgcolor: economicsSubtab === 'developer' ? 'rgba(178,140,80,0.15)' : 'transparent',
                                    border: economicsSubtab === 'developer' ? '1px solid rgba(178,140,80,0.3)' : '1px solid transparent',
                                    borderRadius: 1,
                                    '&:hover': { bgcolor: 'rgba(178,140,80,0.1)' },
                                }}
                            >
                                Developer
                            </Button>
                            <Button
                                onClick={() => setEconomicsSubtab('customer')}
                                sx={{
                                    px: 2,
                                    py: 0.75,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    color: economicsSubtab === 'customer' ? '#b28c50' : isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                    bgcolor: economicsSubtab === 'customer' ? 'rgba(178,140,80,0.15)' : 'transparent',
                                    border: economicsSubtab === 'customer' ? '1px solid rgba(178,140,80,0.3)' : '1px solid transparent',
                                    borderRadius: 1,
                                    '&:hover': { bgcolor: 'rgba(178,140,80,0.1)' },
                                }}
                            >
                                Customer
                            </Button>
                            <Button
                                onClick={() => setEconomicsSubtab('cashflows')}
                                sx={{
                                    px: 2,
                                    py: 0.75,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    color: economicsSubtab === 'cashflows' ? '#b28c50' : isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                    bgcolor: economicsSubtab === 'cashflows' ? 'rgba(178,140,80,0.15)' : 'transparent',
                                    border: economicsSubtab === 'cashflows' ? '1px solid rgba(178,140,80,0.3)' : '1px solid transparent',
                                    borderRadius: 1,
                                    '&:hover': { bgcolor: 'rgba(178,140,80,0.1)' },
                                }}
                            >
                                Cash Flows
                            </Button>
                        </Box>

                        {/* Commercial Assumptions - shown on all subtabs */}
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 3,
                            mb: 3,
                            p: 2,
                            borderRadius: 1,
                            bgcolor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                            border: isRetro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                        }}>
                            <Typography
                                sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                    letterSpacing: '0.05em',
                                    textTransform: 'uppercase',
                                    width: '100%',
                                    mb: -1,
                                }}
                            >
                                Commercial Assumptions
                            </Typography>
                            {/* Get assumptions from first scenario as reference (typically same across scenarios) */}
                            {(() => {
                                const assumptions = getCommercialAssumptions(scenarios[0]);
                                return (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                                                Analysis Period:
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', fontWeight: 500 }}>
                                                {assumptions.analysisYears} years
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                                                Discount Rate:
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', fontWeight: 500 }}>
                                                {assumptions.discountRate}%
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                                                Electricity Escalation:
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', fontWeight: 500 }}>
                                                {(assumptions.electricityEscalation * 100).toFixed(1)}%/yr
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                                                O&M Escalation:
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', fontWeight: 500 }}>
                                                {(assumptions.omEscalation * 100).toFixed(1)}%/yr
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                                                ESA Escalation:
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', fontWeight: 500 }}>
                                                {(assumptions.esaEscalation * 100).toFixed(1)}%/yr
                                            </Typography>
                                        </Box>
                                    </>
                                );
                            })()}
                        </Box>

                        {/* DEVELOPER SUBTAB */}
                        {economicsSubtab === 'developer' && (
                            <Box>
                                {/* Developer Returns - FIRST */}
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        mb: 2,
                                    }}
                                >
                                    Developer Returns
                                </Typography>
                                <Box sx={{ overflowX: 'auto', mb: 4 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '2px solid #b28c50', fontSize: '0.75rem', fontWeight: 600, color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', textTransform: 'uppercase' }}>
                                                    Metric
                                                </th>
                                                {scenarios.map((s, idx) => (
                                                    <th key={s.id} style={{ textAlign: 'right', padding: '12px 16px', borderBottom: `2px solid ${SCENARIO_COLORS[idx]}`, fontSize: '0.85rem', fontWeight: 600, color: SCENARIO_COLORS[idx] }}>
                                                        {s.name}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* IRR */}
                                            <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <TimelineIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                                        IRR
                                                    </Box>
                                                </td>
                                                {scenarios.map(s => {
                                                    const deal = getDealAnalysis(s);
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: deal?.irr ? 'rgba(255,255,255,0.8)' : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>
                                                            {deal?.irr ? `${(deal.irr * 100).toFixed(1)}%` : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* Payback */}
                                            <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <SpeedIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                                        Simple Payback
                                                    </Box>
                                                </td>
                                                {scenarios.map((s, idx) => {
                                                    const financial = getFinancialSummary(s);
                                                    const validPaybacks = scoredScenarios.filter(sc => sc.payback > 0 && sc.payback < 999);
                                                    const isBest = validPaybacks.length > 0 && idx === scoredScenarios.reduce((best, curr, i, arr) => {
                                                        if (curr.payback <= 0 || curr.payback >= 999) return best;
                                                        if (arr[best].payback <= 0 || arr[best].payback >= 999) return i;
                                                        return (curr.payback < arr[best].payback) ? i : best;
                                                    }, 0);
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: isBest ? '#22c55e' : isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', fontWeight: isBest ? 700 : 400 }}>
                                                            {formatPayback(financial.paybackPeriod)}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* Year 1 ESA Revenue */}
                                            <tr>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <AttachMoneyIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                                        Year 1 ESA Revenue
                                                    </Box>
                                                </td>
                                                {scenarios.map(s => {
                                                    const deal = getDealAnalysis(s);
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: deal?.year1EsaPayment ? '#22c55e' : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>
                                                            {deal?.year1EsaPayment ? formatCurrency(deal.year1EsaPayment) : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        </tbody>
                                    </table>
                                </Box>

                                {/* Capital Costs - SECOND */}
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        mb: 2,
                                    }}
                                >
                                    Capital Costs
                                </Typography>
                                <Box sx={{ overflowX: 'auto', mb: 4 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={{
                                                    textAlign: 'left',
                                                    padding: '12px 16px',
                                                    borderBottom: '2px solid #b28c50',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                                                    textTransform: 'uppercase',
                                                }}>
                                                    Line Item
                                                </th>
                                                {scenarios.map((s, idx) => (
                                                    <th key={s.id} style={{
                                                        textAlign: 'right',
                                                        padding: '12px 16px',
                                                        borderBottom: `2px solid ${SCENARIO_COLORS[idx]}`,
                                                        fontSize: '0.85rem',
                                                        fontWeight: 600,
                                                        color: SCENARIO_COLORS[idx],
                                                    }}>
                                                        {s.name}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* Solar - only show if any scenario has PV */}
                                            {anyScenarioHasPV && (
                                                <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                                    <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                        Solar PV
                                                    </td>
                                                    {scenarios.map(s => {
                                                        const capex = getCapexBreakdown(s);
                                                        return (
                                                            <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                                {capex?.solar ? formatCurrency(capex.solar) : '—'}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            )}
                                            {/* Storage - only show if any scenario has battery */}
                                            {anyScenarioHasStorage && (
                                                <tr>
                                                    <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                        Battery Storage
                                                    </td>
                                                    {scenarios.map(s => {
                                                        const capex = getCapexBreakdown(s);
                                                        return (
                                                            <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                                {capex?.storage ? formatCurrency(capex.storage) : '—'}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            )}
                                            {/* Generator - only show if any scenario has generator */}
                                            {anyScenarioHasGenerator && (
                                                <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                                    <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                        Generator
                                                    </td>
                                                    {scenarios.map(s => {
                                                        const capex = getCapexBreakdown(s);
                                                        return (
                                                            <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                                {capex?.generator ? formatCurrency(capex.generator) : '—'}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            )}
                                            {/* Soft Costs / BOS */}
                                            <tr>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    Soft Costs / BOS
                                                </td>
                                                {scenarios.map(s => {
                                                    const capex = getCapexBreakdown(s);
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                            {(capex?.bos || capex?.soft_costs) ? formatCurrency(capex.bos || capex.soft_costs) : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* Total CapEx */}
                                            <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', borderTop: isRetro ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)', fontWeight: 600 }}>
                                                    Total CapEx
                                                </td>
                                                {scenarios.map(s => {
                                                    const capex = getCapexBreakdown(s);
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)', fontWeight: 600 }}>
                                                            {capex?.total ? formatCurrency(capex.total) : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* ITC Credit */}
                                            <tr>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: '#22c55e' }}>
                                                    ITC Credit
                                                </td>
                                                {scenarios.map(s => {
                                                    const capex = getCapexBreakdown(s);
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: '#22c55e' }}>
                                                            {capex?.itc_amount ? `(${formatCurrency(capex.itc_amount)})` : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* Net CapEx */}
                                            <tr style={{ backgroundColor: 'rgba(178,140,80,0.1)', borderTop: '2px solid rgba(178,140,80,0.5)' }}>
                                                <td style={{ padding: '14px 16px', fontSize: '0.9rem', color: '#b28c50', fontWeight: 700 }}>
                                                    Net CapEx (After ITC)
                                                </td>
                                                {scenarios.map(s => {
                                                    const capex = getCapexBreakdown(s);
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '14px 16px', fontSize: '1rem', fontFamily: vortxTypography.mono, color: '#b28c50', fontWeight: 700 }}>
                                                            {capex?.net_capex ? formatCurrency(capex.net_capex) : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        </tbody>
                                    </table>
                                </Box>

                                {/* Operating Costs - THIRD */}
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        mb: 2,
                                    }}
                                >
                                    Operating Costs
                                </Typography>
                                <Box sx={{ overflowX: 'auto', mb: 4 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '2px solid #b28c50', fontSize: '0.75rem', fontWeight: 600, color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', textTransform: 'uppercase' }}>
                                                    Line Item
                                                </th>
                                                {scenarios.map((s, idx) => (
                                                    <th key={s.id} style={{ textAlign: 'right', padding: '12px 16px', borderBottom: `2px solid ${SCENARIO_COLORS[idx]}`, fontSize: '0.85rem', fontWeight: 600, color: SCENARIO_COLORS[idx] }}>
                                                        {s.name}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* O&M - Solar - only show if any scenario has PV */}
                                            {anyScenarioHasPV && (
                                                <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                                    <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                        Solar O&M
                                                    </td>
                                                    {scenarios.map(s => {
                                                        const opex = getOpexBreakdown(s);
                                                        return (
                                                            <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                                {opex?.pvOM ? formatCurrency(opex.pvOM) : '—'}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            )}
                                            {/* O&M - Storage - only show if any scenario has battery */}
                                            {anyScenarioHasStorage && (
                                                <tr>
                                                    <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                        Storage O&M
                                                    </td>
                                                    {scenarios.map(s => {
                                                        const opex = getOpexBreakdown(s);
                                                        return (
                                                            <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                                {opex?.batteryOM ? formatCurrency(opex.batteryOM) : '—'}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            )}
                                            {/* O&M - Generator - only show if any scenario has generator */}
                                            {anyScenarioHasGenerator && (
                                                <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                                    <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                        Generator O&M
                                                    </td>
                                                    {scenarios.map(s => {
                                                        const opex = getOpexBreakdown(s);
                                                        return (
                                                            <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                                {opex?.generatorOM ? formatCurrency(opex.generatorOM) : '—'}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            )}
                                            {/* Fixed Annual Costs (Insurance, Asset Mgmt, etc.) */}
                                            <tr>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    Other Fixed Costs
                                                </td>
                                                {scenarios.map(s => {
                                                    const opex = getOpexBreakdown(s);
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                            {opex?.fixedAnnualOM ? formatCurrency(opex.fixedAnnualOM) : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* Total Year 1 OpEx */}
                                            <tr style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderTop: '2px solid rgba(239,68,68,0.5)' }}>
                                                <td style={{ padding: '14px 16px', fontSize: '0.9rem', color: '#ef4444', fontWeight: 700 }}>
                                                    Total Year 1 OpEx
                                                </td>
                                                {scenarios.map(s => {
                                                    const opex = getOpexBreakdown(s);
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '14px 16px', fontSize: '1rem', fontFamily: vortxTypography.mono, color: '#ef4444', fontWeight: 700 }}>
                                                            {opex?.totalAnnualOM ? formatCurrency(opex.totalAnnualOM) : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        </tbody>
                                    </table>
                                </Box>
                            </Box>
                        )}

                        {/* CUSTOMER SUBTAB */}
                        {economicsSubtab === 'customer' && (
                            <Box>
                                {/* Customer Economics - FIRST (was Lifecycle Economics) */}
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        mb: 2,
                                    }}
                                >
                                    Customer Economics
                                </Typography>
                                <Box sx={{ overflowX: 'auto', mb: 4 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '2px solid #b28c50', fontSize: '0.75rem', fontWeight: 600, color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', textTransform: 'uppercase' }}>
                                                    Metric
                                                </th>
                                                {scenarios.map((s, idx) => (
                                                    <th key={s.id} style={{ textAlign: 'right', padding: '12px 16px', borderBottom: `2px solid ${SCENARIO_COLORS[idx]}`, fontSize: '0.85rem', fontWeight: 600, color: SCENARIO_COLORS[idx] }}>
                                                        {s.name}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* Customer NPV */}
                                            <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <TrendingUpIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                                        NPV
                                                    </Box>
                                                </td>
                                                {scenarios.map(s => {
                                                    const financial = getFinancialSummary(s);
                                                    const deal = getDealAnalysis(s);
                                                    // Prefer deal analysis customerNpv, fallback to financial npv
                                                    const npv = deal?.customerNpv ?? financial.npv;
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, fontWeight: 600, color: npv >= 0 ? '#22c55e' : '#ef4444' }}>
                                                            {npv != null ? formatCurrency(npv) : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* Effective Rate */}
                                            <tr>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    Effective Rate
                                                </td>
                                                {scenarios.map(s => {
                                                    const deal = getDealAnalysis(s);
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)' }}>
                                                            {deal?.effectiveRate != null ? `$${deal.effectiveRate.toFixed(4)}/kWh` : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* BAU Rate */}
                                            <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    BAU Rate
                                                </td>
                                                {scenarios.map(s => {
                                                    const deal = getDealAnalysis(s);
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                                                            {deal?.bauRate != null ? `$${deal.bauRate.toFixed(4)}/kWh` : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* Rate Discount vs. Utility */}
                                            <tr>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    Rate Discount vs. Utility
                                                </td>
                                                {scenarios.map(s => {
                                                    const deal = getDealAnalysis(s);
                                                    return (
                                                        <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, color: deal?.rateDiscount > 0 ? '#22c55e' : isRetro ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)' }}>
                                                            {deal?.rateDiscount != null ? `${(deal.rateDiscount * 100).toFixed(1)}%` : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        </tbody>
                                    </table>
                                </Box>

                                {/* Year 1 Savings & Costs Breakdown */}
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        mb: 2,
                                    }}
                                >
                                    Year 1 Savings & Costs Breakdown
                                </Typography>
                                <Box sx={{ overflowX: 'auto', mb: 4 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={{
                                                    textAlign: 'left',
                                                    padding: '12px 16px',
                                                    borderBottom: '2px solid #b28c50',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                                                    textTransform: 'uppercase',
                                                }}>
                                                    Line Item
                                                </th>
                                                {scenarios.map((s, idx) => (
                                                    <th key={s.id} style={{
                                                        textAlign: 'right',
                                                        padding: '12px 16px',
                                                        borderBottom: `2px solid ${SCENARIO_COLORS[idx]}`,
                                                        fontSize: '0.85rem',
                                                        fontWeight: 600,
                                                        color: SCENARIO_COLORS[idx],
                                                    }}>
                                                        {s.name}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* Energy Charges Avoided */}
                                            <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    Energy Savings
                                                </td>
                                                {scenarios.map(s => {
                                                    const details = getDetailedSavings(s);
                                                    const hasValue = details && details.energySavings > 0;
                                                    return (
                                                        <td key={s.id} style={{
                                                            textAlign: 'right',
                                                            padding: '12px 16px',
                                                            fontSize: '0.95rem',
                                                            fontFamily: vortxTypography.mono,
                                                            color: hasValue ? 'rgba(255,255,255,0.8)' : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                        }}>
                                                            {details ? (hasValue ? formatCurrency(details.energySavings) : '—') : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* Demand Charges Avoided */}
                                            <tr>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    Demand Savings
                                                </td>
                                                {scenarios.map(s => {
                                                    const details = getDetailedSavings(s);
                                                    const hasValue = details && details.demandSavings > 0;
                                                    return (
                                                        <td key={s.id} style={{
                                                            textAlign: 'right',
                                                            padding: '12px 16px',
                                                            fontSize: '0.95rem',
                                                            fontFamily: vortxTypography.mono,
                                                            color: hasValue ? 'rgba(255,255,255,0.8)' : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                        }}>
                                                            {details ? (hasValue ? formatCurrency(details.demandSavings) : '—') : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* Fuel Costs - only show if any scenario has generator */}
                                            {anyScenarioHasGenerator && (
                                                <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                                    <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                        Fuel Costs (Generator)
                                                    </td>
                                                    {scenarios.map(s => {
                                                        const details = getDetailedSavings(s);
                                                        const hasValue = details && details.fuelCosts > 0;
                                                        return (
                                                            <td key={s.id} style={{
                                                                textAlign: 'right',
                                                                padding: '12px 16px',
                                                                fontSize: '0.95rem',
                                                                fontFamily: vortxTypography.mono,
                                                                color: hasValue ? 'rgba(255,255,255,0.8)' : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                            }}>
                                                                {details ? (hasValue ? formatCurrency(details.fuelCosts) : '—') : '—'}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            )}
                                            {/* Standby Charges */}
                                            <tr>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    Standby Charges
                                                </td>
                                                {scenarios.map(s => {
                                                    const details = getDetailedSavings(s);
                                                    const hasValue = details && details.standbyCharges > 0;
                                                    return (
                                                        <td key={s.id} style={{
                                                            textAlign: 'right',
                                                            padding: '12px 16px',
                                                            fontSize: '0.95rem',
                                                            fontFamily: vortxTypography.mono,
                                                            color: hasValue ? 'rgba(255,255,255,0.8)' : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                        }}>
                                                            {details ? (hasValue ? formatCurrency(details.standbyCharges) : '—') : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* Departing Load Charges */}
                                            <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    Departing Load Charges
                                                </td>
                                                {scenarios.map(s => {
                                                    const details = getDetailedSavings(s);
                                                    const hasValue = details && details.departingLoadCharges > 0;
                                                    return (
                                                        <td key={s.id} style={{
                                                            textAlign: 'right',
                                                            padding: '12px 16px',
                                                            fontSize: '0.95rem',
                                                            fontFamily: vortxTypography.mono,
                                                            color: hasValue ? 'rgba(255,255,255,0.8)' : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                        }}>
                                                            {details ? (hasValue ? formatCurrency(details.departingLoadCharges) : '—') : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* ESA Payment */}
                                            <tr>
                                                <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                    ESA Payment
                                                </td>
                                                {scenarios.map(s => {
                                                    const deal = getDealAnalysis(s);
                                                    const hasValue = deal?.year1EsaPayment > 0;
                                                    return (
                                                        <td key={s.id} style={{
                                                            textAlign: 'right',
                                                            padding: '12px 16px',
                                                            fontSize: '0.95rem',
                                                            fontFamily: vortxTypography.mono,
                                                            color: hasValue ? 'rgba(255,255,255,0.8)' : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                        }}>
                                                            {hasValue ? formatCurrency(deal.year1EsaPayment) : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {/* Net Savings (Total) */}
                                            <tr style={{ borderTop: '2px solid rgba(178,140,80,0.5)' }}>
                                                <td style={{ padding: '14px 16px', fontSize: '0.9rem', color: '#b28c50', fontWeight: 600 }}>
                                                    Net Savings
                                                </td>
                                                {scenarios.map(s => {
                                                    const details = getDetailedSavings(s);
                                                    const deal = getDealAnalysis(s);
                                                    const esaPayment = deal?.year1EsaPayment || 0;
                                                    const netSavings = details
                                                        ? (details.energySavings || 0) + (details.demandSavings || 0)
                                                          - (details.fuelCosts || 0) - (details.standbyCharges || 0)
                                                          - (details.departingLoadCharges || 0) - esaPayment
                                                        : null;
                                                    return (
                                                        <td key={s.id} style={{
                                                            textAlign: 'right',
                                                            padding: '14px 16px',
                                                            fontSize: '1.0rem',
                                                            fontWeight: 600,
                                                            fontFamily: vortxTypography.mono,
                                                            color: netSavings != null ? (netSavings >= 0 ? '#4caf50' : '#f44336') : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                        }}>
                                                            {netSavings != null ? formatCurrency(netSavings) : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        </tbody>
                                    </table>
                                </Box>
                            </Box>
                        )}

                        {/* CASHFLOWS SUBTAB */}
                        {economicsSubtab === 'cashflows' && (
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        mb: 3,
                                        textAlign: 'center',
                                    }}
                                >
                                    Cumulative Cash Flow Projection
                                </Typography>
                                <CashFlowChart scenarios={scenarios} getCashFlows={getCashFlows} getFinancialSummary={getFinancialSummary} />

                                {/* Legend */}
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2, mb: 4 }}>
                                    {scenarios.map((s, idx) => (
                                        <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: '50%',
                                                    bgcolor: SCENARIO_COLORS[idx],
                                                }}
                                            />
                                            <Typography sx={{ fontSize: '0.8rem', color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
                                                {s.name}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>

                                {/* Year-by-Year Table */}
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        mb: 2,
                                    }}
                                >
                                    Year-by-Year Cash Flows
                                </Typography>
                                <Box sx={{ overflowX: 'auto', maxHeight: 400 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            {/* Scenario name row */}
                                            <tr>
                                                <th rowSpan={2} style={{
                                                    textAlign: 'left',
                                                    padding: '8px 12px',
                                                    borderBottom: '2px solid #b28c50',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                                                    position: 'sticky',
                                                    top: 0,
                                                    backgroundColor: isRetro ? '#1a1a1a' : '#f5f5f5',
                                                    zIndex: 2,
                                                    minWidth: 50,
                                                }}>
                                                    Year
                                                </th>
                                                {scenarios.map((s, idx) => (
                                                    <th key={s.id} colSpan={4} style={{
                                                        textAlign: 'center',
                                                        padding: '8px 4px',
                                                        borderBottom: `2px solid ${SCENARIO_COLORS[idx]}`,
                                                        fontSize: '0.8rem',
                                                        fontWeight: 600,
                                                        color: SCENARIO_COLORS[idx],
                                                        position: 'sticky',
                                                        top: 0,
                                                        backgroundColor: isRetro ? '#1a1a1a' : '#f5f5f5',
                                                        zIndex: 1,
                                                    }}>
                                                        {s.name}
                                                    </th>
                                                ))}
                                            </tr>
                                            {/* Sub-header row */}
                                            <tr>
                                                {scenarios.map((s, idx) => (
                                                    <React.Fragment key={s.id}>
                                                        <th style={{ textAlign: 'right', padding: '4px 8px', fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', backgroundColor: isRetro ? '#1a1a1a' : '#f5f5f5', position: 'sticky', top: 28, borderBottom: isRetro ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)', borderLeft: `2px solid ${SCENARIO_COLORS[idx]}` }}>Savings</th>
                                                        <th style={{ textAlign: 'right', padding: '4px 8px', fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', backgroundColor: isRetro ? '#1a1a1a' : '#f5f5f5', position: 'sticky', top: 28, borderBottom: isRetro ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>Costs</th>
                                                        <th style={{ textAlign: 'right', padding: '4px 8px', fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', backgroundColor: isRetro ? '#1a1a1a' : '#f5f5f5', position: 'sticky', top: 28, borderBottom: isRetro ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>Net</th>
                                                        <th style={{ textAlign: 'right', padding: '4px 8px', fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontWeight: 600, backgroundColor: isRetro ? '#1a1a1a' : '#f5f5f5', position: 'sticky', top: 28, borderBottom: isRetro ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>Cumulative</th>
                                                    </React.Fragment>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.from({ length: 26 }, (_, year) => (
                                                <tr key={year} style={{ backgroundColor: year % 2 === 0 ? (isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)') : 'transparent' }}>
                                                    <td style={{ padding: '6px 12px', fontSize: '0.8rem', color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                                                        {year}
                                                    </td>
                                                    {scenarios.map((s, idx) => {
                                                        const flows = getCashFlows(s);
                                                        const financial = getFinancialSummary(s);
                                                        const yearFlow = year > 0 ? flows.find(f => f.year === year) : null;
                                                        const borderStyle = `2px solid ${SCENARIO_COLORS[idx]}`;

                                                        // Year 0 is initial investment
                                                        if (year === 0) {
                                                            const investment = financial.totalCapex || 0;
                                                            return (
                                                                <React.Fragment key={s.id}>
                                                                    <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.75rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', borderLeft: borderStyle }}>—</td>
                                                                    <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.75rem', fontFamily: vortxTypography.mono, color: '#ef4444' }}>{formatCurrency(-investment, true)}</td>
                                                                    <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.75rem', fontFamily: vortxTypography.mono, color: '#ef4444' }}>{formatCurrency(-investment, true)}</td>
                                                                    <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.8rem', fontFamily: vortxTypography.mono, fontWeight: 600, color: '#ef4444' }}>{formatCurrency(-investment, true)}</td>
                                                                </React.Fragment>
                                                            );
                                                        }

                                                        // Years 1+: show breakdown
                                                        const savings = yearFlow?.savings ?? yearFlow?.energySavings ?? 0;
                                                        const costs = yearFlow?.om_costs ?? yearFlow?.omCosts ?? 0;
                                                        const netCashflow = yearFlow?.net_cashflow ?? yearFlow?.netCashflow ?? 0;
                                                        const cumulative = yearFlow?.cumulative ?? yearFlow?.cumulative_cashflow ?? yearFlow?.cumulativeCashflow;

                                                        if (!yearFlow) {
                                                            return (
                                                                <React.Fragment key={s.id}>
                                                                    <td style={{ textAlign: 'right', padding: '6px 8px', color: isRetro ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', borderLeft: borderStyle }}>—</td>
                                                                    <td style={{ textAlign: 'right', padding: '6px 8px', color: isRetro ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }}>—</td>
                                                                    <td style={{ textAlign: 'right', padding: '6px 8px', color: isRetro ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }}>—</td>
                                                                    <td style={{ textAlign: 'right', padding: '6px 8px', color: isRetro ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }}>—</td>
                                                                </React.Fragment>
                                                            );
                                                        }

                                                        return (
                                                            <React.Fragment key={s.id}>
                                                                <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.75rem', fontFamily: vortxTypography.mono, color: '#22c55e', borderLeft: borderStyle }}>{formatCurrency(savings, true)}</td>
                                                                <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.75rem', fontFamily: vortxTypography.mono, color: '#ef4444' }}>{formatCurrency(-costs, true)}</td>
                                                                <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.75rem', fontFamily: vortxTypography.mono, color: netCashflow >= 0 ? '#22c55e' : '#ef4444' }}>{formatCurrency(netCashflow, true)}</td>
                                                                <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.8rem', fontFamily: vortxTypography.mono, fontWeight: 600, color: cumulative >= 0 ? '#22c55e' : '#ef4444' }}>{cumulative !== undefined ? formatCurrency(cumulative, true) : '—'}</td>
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Box>
                            </Box>
                        )}
                    </Box>
                    )
                )}

                {/* VPP REVENUE TAB */}
                {/* BUDGET TAB */}
                {viewMode === 'advanced' && activeSection === 'budget' && (
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                mb: 3,
                                textAlign: 'center',
                            }}
                        >
                            Capital Expenditure
                        </Typography>
                        {(() => {
                            // Compute budget data per scenario
                            const budgetData = scenarios.map(scenario => {
                                const budget = scenario.ui_state?.budget;
                                const capexInputs = budget?.capexInputs;
                                const itcInputs = budget?.itcInputs;
                                const includeItc = budget?.includeItc ?? false;
                                const opexInputs = budget?.opexInputs;
                                const sys = getSystemConfig(scenario);
                                const systemSizes = {
                                    pvKw: sys.pvKw || 0,
                                    batteryKw: sys.storageKw || 0,
                                    batteryKwh: sys.storageKwh || 0,
                                    generatorKw: sys.generatorKw || 0,
                                };

                                if (!capexInputs) return null;

                                const capex = calculateCapex(systemSizes, capexInputs);
                                const itc = calculateItc(capex, itcInputs || {}, includeItc, systemSizes);
                                const opex = opexInputs ? calculateOpex(systemSizes, opexInputs) : null;

                                return { capex, itc, opex, systemSizes, includeItc };
                            });

                            const hasBudget = budgetData.some(d => d !== null);
                            if (!hasBudget) {
                                return (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography sx={{ color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', fontSize: '0.9rem' }}>
                                            No budget data configured for these scenarios.
                                        </Typography>
                                        <Typography sx={{ color: isRetro ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', fontSize: '0.75rem', mt: 1 }}>
                                            Configure CapEx in the Budget tab of the Scenario Editor.
                                        </Typography>
                                    </Box>
                                );
                            }

                            // Build CapEx rows
                            const capexRows = [
                                {
                                    label: 'PV Turnkey',
                                    getSize: (d) => d.systemSizes.pvKw > 0 ? `${d.systemSizes.pvKw.toLocaleString()} kW` : '—',
                                    getUpfront: (d) => d.capex.components.pvTurnkey.total,
                                    getItcPct: (d) => d.itc.pvTurnkey.itcPercent,
                                    getNet: (d) => d.itc.pvTurnkey.netCost,
                                },
                                {
                                    label: 'Battery Equipment',
                                    getSize: (d) => d.systemSizes.batteryKwh > 0 ? `${d.systemSizes.batteryKwh.toLocaleString()} kWh` : '—',
                                    getUpfront: (d) => d.capex.components.battery.equipment,
                                    getItcPct: (d) => d.itc.batteryEquipment.itcPercent,
                                    getNet: (d) => d.itc.batteryEquipment.netCost,
                                },
                                {
                                    label: 'Battery Installation',
                                    getSize: () => '',
                                    getUpfront: (d) => d.capex.components.battery.installation,
                                    getItcPct: (d) => d.itc.batteryInstallation.itcPercent,
                                    getNet: (d) => d.itc.batteryInstallation.netCost,
                                },
                                {
                                    label: 'Generator Equipment',
                                    getSize: (d) => d.systemSizes.generatorKw > 0 ? `${d.systemSizes.generatorKw.toLocaleString()} kW` : '—',
                                    getUpfront: (d) => d.capex.components.generator.equipment,
                                    getItcPct: (d) => d.itc.generatorEquipment.itcPercent,
                                    getNet: (d) => d.itc.generatorEquipment.netCost,
                                },
                                {
                                    label: 'Generator Installation',
                                    getSize: () => '',
                                    getUpfront: (d) => d.capex.components.generator.installation,
                                    getItcPct: (d) => d.itc.generatorInstallation.itcPercent,
                                    getNet: (d) => d.itc.generatorInstallation.netCost,
                                },
                            ];

                            const softCostRows = [
                                { label: 'Engineering', key: 'engineering' },
                                { label: 'Permits', key: 'permits' },
                                { label: 'Project Management', key: 'projectManagement' },
                                { label: 'Commissioning', key: 'commissioning' },
                                { label: 'Contingency', key: 'contingency' },
                                { label: 'Custom', key: 'custom' },
                            ];

                            const fmtCurr = (val) => {
                                if (val == null || val === 0) return '—';
                                return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
                            };

                            return (
                                <>
                                    <Box sx={{ borderRadius: 2, border: isRetro ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)', overflow: 'hidden', mb: 3 }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid rgba(178,140,80,0.3)' }}>
                                                    <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.75rem', fontWeight: 600, color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                        Component
                                                    </th>
                                                    {scenarios.map((s, idx) => (
                                                        <th key={s.id} colSpan={3} style={{
                                                            textAlign: 'center',
                                                            padding: '12px 8px',
                                                            fontSize: '0.8rem',
                                                            fontWeight: 600,
                                                            color: SCENARIO_COLORS[idx],
                                                            borderLeft: idx > 0 ? (isRetro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)') : 'none',
                                                        }}>
                                                            {s.name}
                                                        </th>
                                                    ))}
                                                </tr>
                                                <tr style={{ borderBottom: isRetro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)' }}>
                                                    <th style={{ padding: '6px 16px' }} />
                                                    {scenarios.map((s, idx) => (
                                                        <React.Fragment key={s.id}>
                                                            <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)', fontWeight: 500, borderLeft: idx > 0 ? (isRetro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)') : 'none' }}>Upfront</th>
                                                            <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)', fontWeight: 500 }}>ITC%</th>
                                                            <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)', fontWeight: 500 }}>Net Cost</th>
                                                        </React.Fragment>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {/* Equipment & Installation rows */}
                                                {capexRows.map((row, rIdx) => {
                                                    // Skip row if all scenarios have 0 upfront for it
                                                    const allZero = budgetData.every(d => !d || row.getUpfront(d) === 0);
                                                    if (allZero) return null;
                                                    return (
                                                        <tr key={row.label} style={{ backgroundColor: rIdx % 2 === 0 ? (isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)') : 'transparent' }}>
                                                            <td style={{ padding: '10px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                                {row.label}
                                                            </td>
                                                            {scenarios.map((s, idx) => {
                                                                const d = budgetData[idx];
                                                                if (!d) return (
                                                                    <React.Fragment key={s.id}>
                                                                        <td colSpan={3} style={{ textAlign: 'center', padding: '10px 8px', color: isRetro ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', fontSize: '0.8rem', borderLeft: idx > 0 ? (isRetro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)') : 'none' }}>—</td>
                                                                    </React.Fragment>
                                                                );
                                                                return (
                                                                    <React.Fragment key={s.id}>
                                                                        <td style={{ textAlign: 'right', padding: '10px 8px', fontSize: '0.85rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', borderLeft: idx > 0 ? (isRetro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)') : 'none' }}>
                                                                            {fmtCurr(row.getUpfront(d))}
                                                                        </td>
                                                                        <td style={{ textAlign: 'right', padding: '10px 8px', fontSize: '0.8rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                                                                            {d.includeItc && row.getItcPct(d) > 0 ? `${row.getItcPct(d)}%` : '—'}
                                                                        </td>
                                                                        <td style={{ textAlign: 'right', padding: '10px 8px', fontSize: '0.85rem', fontFamily: vortxTypography.mono, color: '#5fd4c4', fontWeight: 600 }}>
                                                                            {d.includeItc ? fmtCurr(row.getNet(d)) : fmtCurr(row.getUpfront(d))}
                                                                        </td>
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </tr>
                                                    );
                                                })}

                                                {/* Soft Costs section header */}
                                                {budgetData.some(d => d && d.capex.softCostsTotal > 0) && (
                                                    <>
                                                        <tr style={{ borderTop: isRetro ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)' }}>
                                                            <td style={{ padding: '10px 16px', fontSize: '0.75rem', fontWeight: 600, color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                Soft Costs
                                                            </td>
                                                            {scenarios.map((s) => (
                                                                <td key={s.id} colSpan={3} />
                                                            ))}
                                                        </tr>
                                                        {softCostRows.map((row, rIdx) => {
                                                            const allZero = budgetData.every(d => !d || (d.capex.softCosts[row.key] || 0) === 0);
                                                            if (allZero) return null;
                                                            return (
                                                                <tr key={row.key} style={{ backgroundColor: rIdx % 2 === 0 ? (isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)') : 'transparent' }}>
                                                                    <td style={{ padding: '10px 16px 10px 28px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
                                                                        {row.label}
                                                                    </td>
                                                                    {scenarios.map((s, idx) => {
                                                                        const d = budgetData[idx];
                                                                        if (!d) return (
                                                                            <React.Fragment key={s.id}>
                                                                                <td colSpan={3} style={{ textAlign: 'center', padding: '10px 8px', color: isRetro ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', borderLeft: idx > 0 ? (isRetro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)') : 'none' }}>—</td>
                                                                            </React.Fragment>
                                                                        );
                                                                        const upfront = d.capex.softCosts[row.key] || 0;
                                                                        const itcData = d.itc[row.key];
                                                                        return (
                                                                            <React.Fragment key={s.id}>
                                                                                <td style={{ textAlign: 'right', padding: '10px 8px', fontSize: '0.85rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', borderLeft: idx > 0 ? (isRetro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)') : 'none' }}>
                                                                                    {fmtCurr(upfront)}
                                                                                </td>
                                                                                <td style={{ textAlign: 'right', padding: '10px 8px', fontSize: '0.8rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                                                                                    {d.includeItc && itcData?.itcPercent > 0 ? `${itcData.itcPercent}%` : '—'}
                                                                                </td>
                                                                                <td style={{ textAlign: 'right', padding: '10px 8px', fontSize: '0.85rem', fontFamily: vortxTypography.mono, color: '#5fd4c4', fontWeight: 600 }}>
                                                                                    {d.includeItc ? fmtCurr(itcData?.netCost ?? upfront) : fmtCurr(upfront)}
                                                                                </td>
                                                                            </React.Fragment>
                                                                        );
                                                                    })}
                                                                </tr>
                                                            );
                                                        })}
                                                    </>
                                                )}

                                                {/* Total row */}
                                                <tr style={{ borderTop: '2px solid rgba(178,140,80,0.3)', backgroundColor: 'rgba(178,140,80,0.05)' }}>
                                                    <td style={{ padding: '12px 16px', fontSize: '0.9rem', fontWeight: 700, color: '#b28c50' }}>
                                                        TOTAL
                                                    </td>
                                                    {scenarios.map((s, idx) => {
                                                        const d = budgetData[idx];
                                                        if (!d) return (
                                                            <React.Fragment key={s.id}>
                                                                <td colSpan={3} style={{ textAlign: 'center', padding: '12px 8px', color: isRetro ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', borderLeft: idx > 0 ? (isRetro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)') : 'none' }}>—</td>
                                                            </React.Fragment>
                                                        );
                                                        return (
                                                            <React.Fragment key={s.id}>
                                                                <td style={{ textAlign: 'right', padding: '12px 8px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, fontWeight: 700, color: isRetro ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)', borderLeft: idx > 0 ? (isRetro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)') : 'none' }}>
                                                                    {fmtCurr(d.itc.totalUpfront)}
                                                                </td>
                                                                <td style={{ textAlign: 'right', padding: '12px 8px', fontSize: '0.85rem', fontWeight: 600, color: '#b28c50' }}>
                                                                    {d.includeItc ? `${d.itc.effectiveItcRate}%` : '—'}
                                                                </td>
                                                                <td style={{ textAlign: 'right', padding: '12px 8px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, fontWeight: 700, color: '#5fd4c4' }}>
                                                                    {d.includeItc ? fmtCurr(d.itc.netCostAfterItc) : fmtCurr(d.itc.totalUpfront)}
                                                                </td>
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </Box>

                                    {/* Annual Operating Costs */}
                                    {budgetData.some(d => d?.opex && d.opex.totalAnnualOM > 0) && (
                                        <>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                    letterSpacing: '0.1em',
                                                    textTransform: 'uppercase',
                                                    mb: 2,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                Annual Operating Costs
                                            </Typography>
                                            <Box sx={{ borderRadius: 2, border: isRetro ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: '2px solid rgba(178,140,80,0.3)' }}>
                                                            <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.75rem', fontWeight: 600, color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                Item
                                                            </th>
                                                            {scenarios.map((s, idx) => (
                                                                <th key={s.id} style={{
                                                                    textAlign: 'right',
                                                                    padding: '12px 16px',
                                                                    fontSize: '0.8rem',
                                                                    fontWeight: 600,
                                                                    color: SCENARIO_COLORS[idx],
                                                                }}>
                                                                    {s.name}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {[
                                                            { label: 'PV O&M', key: 'pvOM' },
                                                            { label: 'Battery O&M', key: 'batteryOM' },
                                                            { label: 'Generator O&M', key: 'generatorOM' },
                                                            { label: 'Fixed Annual O&M', key: 'fixedAnnualOM' },
                                                        ].map((row, rIdx) => {
                                                            const allZero = budgetData.every(d => !d?.opex || (d.opex[row.key] || 0) === 0);
                                                            if (allZero) return null;
                                                            return (
                                                                <tr key={row.key} style={{ backgroundColor: rIdx % 2 === 0 ? (isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)') : 'transparent' }}>
                                                                    <td style={{ padding: '10px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                                        {row.label}
                                                                    </td>
                                                                    {scenarios.map((s, idx) => {
                                                                        const d = budgetData[idx];
                                                                        return (
                                                                            <td key={s.id} style={{ textAlign: 'right', padding: '10px 16px', fontSize: '0.85rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                                                                {d?.opex ? fmtCurr(d.opex[row.key]) : '—'}
                                                                            </td>
                                                                        );
                                                                    })}
                                                                </tr>
                                                            );
                                                        })}
                                                        {/* Total OpEx */}
                                                        <tr style={{ borderTop: '2px solid rgba(178,140,80,0.3)', backgroundColor: 'rgba(178,140,80,0.05)' }}>
                                                            <td style={{ padding: '12px 16px', fontSize: '0.9rem', fontWeight: 700, color: '#b28c50' }}>
                                                                TOTAL OpEx
                                                            </td>
                                                            {scenarios.map((s, idx) => {
                                                                const d = budgetData[idx];
                                                                return (
                                                                    <td key={s.id} style={{ textAlign: 'right', padding: '12px 16px', fontSize: '0.95rem', fontFamily: vortxTypography.mono, fontWeight: 700, color: isRetro ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)' }}>
                                                                        {d?.opex ? `${fmtCurr(d.opex.totalAnnualOM)}/yr` : '—'}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </Box>
                                        </>
                                    )}
                                </>
                            );
                        })()}
                    </Box>
                )}

                {viewMode === 'advanced' && activeSection === 'vpp' && (
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                mb: 3,
                                textAlign: 'center',
                            }}
                        >
                            Virtual Power Plant Revenue
                        </Typography>
                        <Box sx={{
                            borderRadius: 2,
                            border: isRetro ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                            overflow: 'hidden',
                        }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid rgba(178,140,80,0.3)' }}>
                                        <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.75rem', fontWeight: 600, color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Metric
                                        </th>
                                        {scenarios.map((s, idx) => (
                                            <th key={s.id} style={{
                                                textAlign: 'right',
                                                padding: '12px 16px',
                                                fontSize: '0.8rem',
                                                fontWeight: 600,
                                                color: SCENARIO_COLORS[idx],
                                            }}>
                                                {s.name}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* VPP Included */}
                                    <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                            VPP Included
                                        </td>
                                        {scenarios.map(s => {
                                            const dealConfig = s.ui_state?.form?.dealAnalysisConfig;
                                            return (
                                                <td key={s.id} style={{
                                                    textAlign: 'right',
                                                    padding: '12px 16px',
                                                    fontSize: '0.9rem',
                                                    fontFamily: vortxTypography.mono,
                                                    color: dealConfig?.vppIncluded ? '#4caf50' : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                }}>
                                                    {dealConfig?.vppIncluded ? 'Yes' : 'No'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* Annual VPP Revenue */}
                                    <tr>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                            Annual VPP Revenue
                                        </td>
                                        {scenarios.map(s => {
                                            const revenue = s.ui_state?.form?.dealAnalysisConfig?.computedResults?.vppAnnualRevenue;
                                            const included = s.ui_state?.form?.dealAnalysisConfig?.vppIncluded;
                                            return (
                                                <td key={s.id} style={{
                                                    textAlign: 'right',
                                                    padding: '12px 16px',
                                                    fontSize: '0.95rem',
                                                    fontWeight: 600,
                                                    fontFamily: vortxTypography.mono,
                                                    color: included && revenue > 0 ? '#4caf50' : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                }}>
                                                    {included && revenue > 0 ? formatCurrency(revenue) : '—'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* Lifetime VPP Revenue */}
                                    <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                            Lifetime VPP Revenue
                                        </td>
                                        {scenarios.map(s => {
                                            const revenue = s.ui_state?.form?.dealAnalysisConfig?.computedResults?.lifetimeVppRevenue;
                                            const included = s.ui_state?.form?.dealAnalysisConfig?.vppIncluded;
                                            return (
                                                <td key={s.id} style={{
                                                    textAlign: 'right',
                                                    padding: '12px 16px',
                                                    fontSize: '0.95rem',
                                                    fontWeight: 600,
                                                    fontFamily: vortxTypography.mono,
                                                    color: included && revenue > 0 ? '#4caf50' : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                }}>
                                                    {included && revenue > 0 ? formatCurrency(revenue) : '—'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* VPP Duration */}
                                    <tr>
                                        <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                                            VPP Duration
                                        </td>
                                        {scenarios.map(s => {
                                            const dealConfig = s.ui_state?.form?.dealAnalysisConfig;
                                            const years = dealConfig?.vppDurationYears;
                                            return (
                                                <td key={s.id} style={{
                                                    textAlign: 'right',
                                                    padding: '12px 16px',
                                                    fontSize: '0.95rem',
                                                    fontFamily: vortxTypography.mono,
                                                    color: dealConfig?.vppIncluded && years ? 'rgba(255,255,255,0.8)' : isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                }}>
                                                    {dealConfig?.vppIncluded && years ? `${years} years` : '—'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </tbody>
                            </table>
                        </Box>
                    </Box>
                )}

            </DialogContent>

            {/* Footer - hide in embedded mode */}
            {!embedded && (
            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: isRetro ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                    bgcolor: 'rgba(0,0,0,0.2)',
                    flexWrap: 'wrap',
                    gap: 1,
                }}
            >
                {/* Export buttons */}
                <Typography sx={{ fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontWeight: 600 }}>
                    Export:
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TableIcon />}
                    onClick={() => exportInvestmentModel(scenarios, projectName)}
                    sx={{
                        color: '#5fd4c4',
                        borderColor: 'rgba(95,212,196,0.3)',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        '&:hover': {
                            borderColor: '#5fd4c4',
                            bgcolor: 'rgba(95,212,196,0.1)',
                        },
                    }}
                >
                    Investment Model
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<GridOnIcon />}
                    onClick={() => exportFullData(scenarios, projectName)}
                    sx={{
                        color: '#5fd4c4',
                        borderColor: 'rgba(95,212,196,0.3)',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        '&:hover': {
                            borderColor: '#5fd4c4',
                            bgcolor: 'rgba(95,212,196,0.1)',
                        },
                    }}
                >
                    Full Data
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PdfIcon />}
                    onClick={() => exportPresentation(scenarios, projectName)}
                    sx={{
                        color: '#5fd4c4',
                        borderColor: 'rgba(95,212,196,0.3)',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        '&:hover': {
                            borderColor: '#5fd4c4',
                            bgcolor: 'rgba(95,212,196,0.1)',
                        },
                    }}
                >
                    Presentation
                </Button>

                <Box sx={{ flex: 1 }} />

                {/* Classic View toggle is in the header — removed duplicate here */}
                <Button
                    onClick={onClose}
                    sx={{
                        px: 3,
                        py: 1,
                        bgcolor: 'rgba(178,140,80,0.1)',
                        color: '#b28c50',
                        border: '1px solid rgba(178,140,80,0.3)',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                            bgcolor: 'rgba(178,140,80,0.2)',
                        },
                    }}
                >
                    Close
                </Button>
            </DialogActions>
            )}
        </Dialog>

        {/* Share Configuration Dialog */}
        <ShareConfigDialog
            open={shareDialogOpen}
            onClose={() => setShareDialogOpen(false)}
            projectId={projectId}
            projectName={projectName}
            scenarioIds={scenarios.map(s => s.id)}
            scenarioNames={scenarios.map(s => s.name)}
            comparisonData={{
                scenarios: scenarios,  // Pass full scenario objects for complete visualization
                projectName,
                capturedAt: new Date().toISOString(),
            }}
        />
        </>
    );
}
